import React, { useEffect, useMemo, useRef, useState, useCallback } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { toast } from "sonner";
import { forceSimulation, forceLink, forceManyBody, forceCenter, forceCollide, forceX, forceY } from "d3-force";
import { api } from "../../lib/apiClient";
import { useAuth } from "../../context/AuthContext";
import { BadgeCheck, AlertCircle, Bookmark, Sparkles, Mountain, Package, Compass, ArrowLeft, Maximize2, X, ZoomIn, ZoomOut, RotateCcw, Frame, Plus, Search, ChevronUp, ChevronDown, ShoppingBag, ExternalLink, Loader2, Users, Globe, Network, Share2, Tag, Hexagon } from "lucide-react";

/**
 * GeneticsMap — strain-centric cannabis pedigree visualization.
 *
 * v3.98 rewrite. Always operates in "centered" mode: the user picks ONE
 * strain (via the prominent strain picker) and we render that strain's
 * full multi-generation pedigree — ancestors stacked above, descendants
 * below, with the picked strain anchored in the middle.
 *
 * Clicking any node RE-CENTERS the pedigree on that node (we re-fetch
 * `/lineage/graph?center=<slug>&depth_up=N&depth_down=M`). The "Open
 * lineage page →" button on the side panel is the only way to navigate
 * away from the map — every other interaction stays inside the map.
 *
 * The global "show every public record" view that used to render the
 * 159-node hairball is gone for good. A strain map without a focus
 * subject isn't a map, it's a wall of nodes.
 */

const CLASS_COLORS = {
  indica:           "#A06CFF",
  indica_dominant:  "#B58CFF",
  sativa:           "#FFB454",
  sativa_dominant:  "#FFC97A",
  hybrid:           "#4FA86F",
  balanced:         "#7CCB99",
  cbd:              "#5FB4E8",
  cbg:              "#5FB4E8",
  high_thc:         "#FF7A6A",
  high_cbd:         "#5FB4E8",
  ruderalis:        "#8C8F95",
  unknown:          "#6B6E73",
  default:          "#7F838B",
};

function nodeAccent(n) {
  if (n.is_stub) return "#6B6E73";
  if (n.verification_status === "disputed") return "#FF7A6A";
  if (n.has_listings) return "#4FA86F";
  if (n.landrace || n.heirloom || n.preservation) return "#D4AF37";
  return CLASS_COLORS[n.classification] || CLASS_COLORS.default;
}

function statusGlow(n) {
  if (n.is_stub) return "rgba(127,131,139,0.25)";
  if (n.has_listings) return "rgba(79,168,111,0.7)";
  if (n.verification_status === "verified") return "rgba(79,168,111,0.55)";
  if (n.verification_status === "disputed") return "rgba(255,122,106,0.55)";
  if (n.verification_status === "breeder_submitted") return "rgba(212,175,55,0.55)";
  return "rgba(143,179,255,0.45)";
}

function truncateLabel(s, max) {
  if (!s) return "";
  const t = String(s).trim();
  return t.length <= max ? t : t.slice(0, max - 1).trim() + "…";
}

const DEFAULT_DEPTH_UP = 3;
const DEFAULT_DEPTH_DOWN = 2;

// Constellation canvas dimensions. We render at a fixed virtual size and let
// pan/zoom transforms handle scale. Keeps the force simulation stable.
const CONSTELLATION_W = 1600;
const CONSTELLATION_H = 1100;

// Classification filter buckets the chips can scope to.
const CLASS_BUCKETS = [
  { id: "indica", label: "Indica",  match: (c) => c === "indica" || c === "indica_dominant", color: CLASS_COLORS.indica },
  { id: "sativa", label: "Sativa",  match: (c) => c === "sativa" || c === "sativa_dominant", color: CLASS_COLORS.sativa },
  { id: "hybrid", label: "Hybrid",  match: (c) => c === "hybrid" || c === "balanced",        color: CLASS_COLORS.hybrid },
];

export default function GeneticsMap({ centerSlug, breederId }) {
  const nav = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { user } = useAuth();
  const isAdmin = !!(user && user.role === "admin");

  // v3.99 — View mode (pedigree | constellation | cluster). Persisted to URL.
  const _initMode = searchParams.get("mode");
  const initialMode = (_initMode === "constellation" || _initMode === "cluster") ? _initMode : "pedigree";
  const [viewMode, setViewMode] = useState(initialMode);

  // v3.99 — Filters (Constellation-mode primarily, also affect pedigree dim).
  const [filterForSale, setFilterForSale] = useState(searchParams.get("sale") === "1");
  const [filterClass, setFilterClass]     = useState(searchParams.get("cls") || "");
  const [filterBreeder, setFilterBreeder] = useState(searchParams.get("breeder") || breederId || "");
  // v3.99c.68 — Advanced Map Modes filter set. Family, canonical, landrace,
  // disputed, seed_type, and admin-mode (+ internal/low-confidence) toggles
  // all sync to the URL alongside the existing filters.
  const [filterFamily, setFilterFamily] = useState(searchParams.get("family") || "");
  const [filterCanonical, setFilterCanonical] = useState(searchParams.get("canon") === "1");
  const [filterLandrace, setFilterLandrace] = useState(searchParams.get("land") === "1");
  const [filterDisputed, setFilterDisputed] = useState(searchParams.get("disp") === "1");
  const [filterSeedType, setFilterSeedType] = useState(searchParams.get("seed") || "");
  // Sibling expansion in pedigree mode (toggle on side panel).
  const [includeSiblings, setIncludeSiblings] = useState(searchParams.get("sib") === "1");
  // Admin-only switches. Only respected by the backend when user is admin.
  const [adminMode, setAdminMode] = useState(isAdmin && searchParams.get("am") === "1");
  const [adminInternal, setAdminInternal] = useState(isAdmin && searchParams.get("ai") === "1");
  const [adminLowConfidence, setAdminLowConfidence] = useState(isAdmin && searchParams.get("alc") === "1");

  // The strain currently anchored at the middle of the pedigree.
  // Initialized from prop; updates when user clicks a node or picks one.
  const [currentCenter, setCurrentCenter] = useState(centerSlug || null);

  // v3.99c — Navigation history stack of previously-anchored strain
  // slugs. Pushed every time the user re-centers (via node click, side-
  // panel descendant click, or strain picker). The Back button pops this
  // stack; when empty it drops the user out to Constellation (the full
  // genetic map library). The "View all" button bypasses history and
  // jumps straight to Constellation.
  const [navStack, setNavStack] = useState([]);
  const navStackRef = useRef(navStack);
  useEffect(() => { navStackRef.current = navStack; }, [navStack]);
  // v3.99c — Read currentCenter from a ref inside recenter so we don't
  // need to nest setNavStack inside setCurrentCenter's functional updater
  // (React StrictMode would double-invoke that nested setter and the
  // stack would grow by 2 per re-center).
  const currentCenterRef = useRef(currentCenter);
  useEffect(() => { currentCenterRef.current = currentCenter; }, [currentCenter]);
  const recenter = useCallback((slug) => {
    if (!slug) return;
    const prev = currentCenterRef.current;
    if (prev && prev !== slug) {
      setNavStack((s) => (s[s.length - 1] === prev ? s : [...s, prev]));
    }
    setCurrentCenter(slug);
    setViewMode("pedigree");
  }, []);
  const goBack = useCallback(() => {
    const s = navStackRef.current;
    if (s.length > 0) {
      const prev = s[s.length - 1];
      setNavStack(s.slice(0, -1));
      setCurrentCenter(prev);
      setViewMode("pedigree");
    } else {
      // v3.99d — Empty stack → show the full pedigree of every strain
      // in the database. We stay in Pedigree mode (no automatic switch
      // to Constellation) and clear `currentCenter` so the fetch effect
      // pulls the global graph and the hierarchical layout renders it
      // as one big tree.
      setCurrentCenter(null);
      setSelected(null);
      setViewMode("pedigree");
    }
  }, []);
  const viewAll = useCallback(() => {
    // v3.99d — Express path to the same "all strains as a pedigree" view.
    setNavStack([]);
    setSelected(null);
    setCurrentCenter(null);
    setViewMode("pedigree");
  }, []);
  const [graph, setGraph] = useState({ nodes: [], edges: [], center_id: null });
  const [loading, setLoading] = useState(true);
  const [bootstrapping, setBootstrapping] = useState(!centerSlug);
  const [centerMissing, setCenterMissing] = useState(false);
  const [hover, setHover] = useState(null);
  // v3.99c.42 — Hover tooltip cursor tracking. The map already maintains
  // a `hover` node id for bloodline highlighting; this adds a tiny screen-
  // space pointer position so the new HoverTooltip card can render near
  // the cursor without flicker. Updated only while a hover is active and
  // the device supports hover (skipped on coarse-pointer touch devices).
  const [hoverPos, setHoverPos] = useState(null);
  const [selected, setSelected] = useState(null);
  const [depthUp, setDepthUp] = useState(DEFAULT_DEPTH_UP);
  const [depthDown, setDepthDown] = useState(DEFAULT_DEPTH_DOWN);
  const [fullscreen, setFullscreen] = useState(false);
  // v3.99c.43 — iPhone black-screen trap kill-switch. Detect coarse-pointer
  // (touch) devices ONCE on mount and treat fullscreen as a hard-disabled
  // feature on them. The previous fix path (browser-back popstate, ESC
  // handler, safe-area padding) was insufficient because the trap entry
  // — the tiny "Expand" button on the top bar — was still tappable, and
  // once activated the iOS notch + collapsing URL bar made the close
  // affordance effectively invisible. We now: (1) hide the Expand button
  // entirely on coarse-pointer, (2) reject setFullscreen(true) calls in a
  // wrapper, (3) render a permanent floating "EXIT MAP" button anchored
  // below the iOS safe-area inset as a final escape hatch in case any
  // path slips through (e.g. someone tests on a hybrid device, or a
  // future code change accidentally calls setFullscreen(true) on touch).
  const [isCoarsePointer, setIsCoarsePointer] = useState(false);
  useEffect(() => {
    if (typeof window === "undefined" || !window.matchMedia) return undefined;
    const mq = window.matchMedia("(pointer: coarse)");
    const sync = () => setIsCoarsePointer(!!mq.matches);
    sync();
    if (mq.addEventListener) mq.addEventListener("change", sync);
    else if (mq.addListener) mq.addListener(sync);
    return () => {
      if (mq.removeEventListener) mq.removeEventListener("change", sync);
      else if (mq.removeListener) mq.removeListener(sync);
    };
  }, []);
  // Belt-and-suspenders: if a coarse-pointer device ends up with
  // fullscreen=true (e.g. via stale state, race, future regression), bail
  // immediately back to inline view. This single useEffect is the last
  // line of defence against the iPhone "tap → black screen → trapped"
  // report that has cost multiple rounds of work.
  useEffect(() => {
    if (isCoarsePointer && fullscreen) setFullscreen(false);
  }, [isCoarsePointer, fullscreen]);
  // Hardened setter — never enters fullscreen on touch.
  const requestFullscreen = useCallback((v) => {
    if (v === true && isCoarsePointer) return;
    setFullscreen(!!v);
  }, [isCoarsePointer]);
  const [view, setView] = useState({ zoom: 1, tx: 0, ty: 0 });
  const svgRef = useRef(null);
  const dragRef = useRef(null);
  const pinchRef = useRef(null);
  const [promotingId, setPromotingId] = useState(null);

  // v3.99 — Constellation-mode state. Rendered nodes carry x/y assigned
  // by the d3-force simulation. We snapshot once after the sim cools so
  // the layout is stable for pan/zoom.
  const [constellation, setConstellation] = useState({ nodes: [], edges: [], byId: new Map(), clusters: new Map() });
  const [constellationLoading, setConstellationLoading] = useState(false);
  // v3.99 — Unified breeders directory used by the breeder filter dropdown.
  // Only loaded for the global map (no embedded breederId prop).
  const [breedersDir, setBreedersDir] = useState({ vendors: [], originators: [] });

  // Sync internal state with prop changes (e.g., user navigates between
  // detail pages with "Map this strain" links).
  useEffect(() => { if (centerSlug) setCurrentCenter(centerSlug); }, [centerSlug]);

  // v3.99 — URL sync. Keeps mode + filters + center + depths reflected so
  // the share-link button just copies window.location. Replace-only so we
  // don't pollute the back stack with every filter toggle.
  //
  // v3.99c.51 — Skip URL writes entirely in embedded mode (breeder storefront,
  // lineage detail, etc. — any mount that passes `breederId`). On those pages
  // the canonical URL is the host page (e.g. /breeder/green-wolf-genetics);
  // appending the map's UI state to it poisoned sharing and SEO with
  // duplicate canonicals like `?center=apple-fritter`. Internal centering
  // still works via component state; first-mount `?center=` is still read,
  // it just isn't re-emitted.
  useEffect(() => {
    if (breederId) return;  // embedded mode — leave the host URL alone
    const next = new URLSearchParams(searchParams);
    // Preserve foreign params (?tab=, ?suggest=, ?center= when set by parent).
    if (viewMode === "constellation") next.set("mode", "constellation");
    else if (viewMode === "cluster") next.set("mode", "cluster");
    else next.delete("mode");
    if (viewMode === "pedigree" && currentCenter && !centerSlug) {
      next.set("center", currentCenter);
    }
    if (viewMode === "pedigree" && depthUp !== DEFAULT_DEPTH_UP) next.set("du", String(depthUp)); else next.delete("du");
    if (viewMode === "pedigree" && depthDown !== DEFAULT_DEPTH_DOWN) next.set("dd", String(depthDown)); else next.delete("dd");
    if (filterForSale) next.set("sale", "1"); else next.delete("sale");
    if (filterClass) next.set("cls", filterClass); else next.delete("cls");
    if (filterBreeder && !breederId) next.set("breeder", filterBreeder); else if (!breederId) next.delete("breeder");
    // v3.99c.68 — Advanced filters in the URL.
    if (filterFamily) next.set("family", filterFamily); else next.delete("family");
    if (filterCanonical) next.set("canon", "1"); else next.delete("canon");
    if (filterLandrace) next.set("land", "1"); else next.delete("land");
    if (filterDisputed) next.set("disp", "1"); else next.delete("disp");
    if (filterSeedType) next.set("seed", filterSeedType); else next.delete("seed");
    if (includeSiblings && viewMode === "pedigree") next.set("sib", "1"); else next.delete("sib");
    if (isAdmin && adminMode) next.set("am", "1"); else next.delete("am");
    if (isAdmin && adminMode && adminInternal) next.set("ai", "1"); else next.delete("ai");
    if (isAdmin && adminMode && adminLowConfidence) next.set("alc", "1"); else next.delete("alc");
    setSearchParams(next, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [viewMode, currentCenter, depthUp, depthDown, filterForSale, filterClass, filterBreeder,
      filterFamily, filterCanonical, filterLandrace, filterDisputed, filterSeedType,
      includeSiblings, adminMode, adminInternal, adminLowConfidence]);

  // v3.99 — Hydrate filter state if the URL already carries depth params on first mount.
  useEffect(() => {
    const du = parseInt(searchParams.get("du") || "", 10);
    const dd = parseInt(searchParams.get("dd") || "", 10);
    if (!Number.isNaN(du) && du >= 1 && du <= 4) setDepthUp(du);
    if (!Number.isNaN(dd) && dd >= 0 && dd <= 3) setDepthDown(dd);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Load breeder directory once for the global map.
  useEffect(() => {
    if (breederId) return undefined;
    let alive = true;
    api.get("/lineage/breeders-directory")
      .then((r) => { if (alive) setBreedersDir({ vendors: r.data?.vendors || [], originators: r.data?.originators || [] }); })
      .catch(() => {});
    return () => { alive = false; };
  }, [breederId]);

  // If no center is supplied, look up a sensible default starting subject.
  // We want a strain with AT LEAST ONE relationship so the first paint
  // shows a real pedigree, not a single isolated node. Strategy: pull a
  // small slice of the graph and pick the most-connected real node.
  useEffect(() => {
    if (viewMode !== "pedigree") return;
    if (currentCenter || !bootstrapping) return;
    let alive = true;
    const fetchDefault = async () => {
      try {
        const params = (filterBreeder || breederId) ? { breeder_id: filterBreeder || breederId, limit: 60 } : { limit: 60 };
        const r = await api.get("/lineage/graph", { params });
        const nodes = r.data?.nodes || [];
        const edges = r.data?.edges || [];
        if (!nodes.length) { if (alive) setBootstrapping(false); return; }
        const edgeCounts = new Map();
        for (const e of edges) {
          edgeCounts.set(e.source, (edgeCounts.get(e.source) || 0) + 1);
          edgeCounts.set(e.target, (edgeCounts.get(e.target) || 0) + 1);
        }
        const candidates = nodes.filter((n) => !n.is_stub);
        const best = candidates.sort((a, b) => {
          const eb = edgeCounts.get(b.id) || 0;
          const ea = edgeCounts.get(a.id) || 0;
          if (eb !== ea) return eb - ea;
          return (b.save_count || 0) - (a.save_count || 0);
        })[0];
        if (alive && best) setCurrentCenter(best.slug || best.id);
      } catch { /* fall through */ }
      finally { if (alive) setBootstrapping(false); }
    };
    fetchDefault();
    return () => { alive = false; };
  }, [viewMode, currentCenter, bootstrapping, breederId, filterBreeder]);

  // v3.99c.68 — Helper that builds the common advanced-filter param set
  // shared by the pedigree, constellation, and cluster fetches.
  const buildAdvancedParams = useCallback((base) => {
    const params = { ...base };
    if (filterFamily) params.family = filterFamily;
    if (filterCanonical) params.canonical_only = "true";
    if (filterLandrace) params.landrace_only = "true";
    if (filterDisputed) params.disputed_only = "true";
    if (filterSeedType) params.seed_type = filterSeedType;
    if (isAdmin && adminMode) {
      params.admin_mode = "true";
      if (adminInternal) params.include_internal = "true";
      if (adminLowConfidence) params.include_low_confidence = "true";
    }
    return params;
  }, [filterFamily, filterCanonical, filterLandrace, filterDisputed, filterSeedType,
      isAdmin, adminMode, adminInternal, adminLowConfidence]);

  // Fetch the pedigree whenever the center or depth changes. When the
  // user has explicitly chosen "View all" (currentCenter === null after
  // bootstrap), we pull the global graph (no `center` param) and the
  // hierarchical layout will render it as a single big pedigree of
  // every public strain in the database. v3.99d.
  useEffect(() => {
    if (viewMode !== "pedigree") return;
    if (!currentCenter && bootstrapping) return; // wait for bootstrap to pick a default
    let alive = true;
    setLoading(true);
    setCenterMissing(false);
    const effBreeder = filterBreeder || breederId;
    const baseParams = currentCenter
      ? { center: currentCenter, depth_up: depthUp, depth_down: depthDown }
      : { limit: 500 };
    if (effBreeder) baseParams.breeder_id = effBreeder;
    if (currentCenter && includeSiblings) baseParams.include_siblings = "true";
    const params = buildAdvancedParams(baseParams);
    api.get("/lineage/graph", { params })
      .then((r) => {
        if (!alive) return;
        setGraph(r.data || { nodes: [], edges: [], center_id: null });
        // For the "view all" global pedigree there's no anchor strain;
        // leave `selected` untouched so the side panel shows the empty/
        // overview state.
        setSelected(currentCenter ? (r.data?.center_id || null) : null);
      })
      .catch(() => {
        if (!alive) return;
        if (currentCenter) {
          setCenterMissing(true);
          setGraph({ nodes: [], edges: [], center_id: null });
        }
      })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [viewMode, currentCenter, depthUp, depthDown, breederId, filterBreeder, bootstrapping, includeSiblings, buildAdvancedParams]);

  // v3.99 — Constellation fetch + force-directed layout. Fired once per
  // breeder/filter change while in constellation mode. Runs the d3-force
  // simulation synchronously to a steady-state (≈300 ticks) so the layout
  // is locked before render — no jitter, deterministic snapshots, easy
  // pan/zoom on top.
  useEffect(() => {
    if (viewMode !== "constellation" && viewMode !== "cluster") return undefined;
    let alive = true;
    setConstellationLoading(true);
    // v3.99c.68 — Cluster mode keeps the same backend graph but renders
    // it with a clustered layout. Both modes share the same fetch.
    const baseParams = { limit: 400 };
    const effBreeder = filterBreeder || breederId;
    if (effBreeder) baseParams.breeder_id = effBreeder;
    const params = buildAdvancedParams(baseParams);
    api.get("/lineage/graph", { params })
      .then((r) => {
        if (!alive) return;
        const rawNodes = r.data?.nodes || [];
        const rawEdges = r.data?.edges || [];
        // Skin nodes for the sim — d3 mutates these.
        const simNodes = rawNodes.map((n) => ({ ...n }));
        const byId = new Map(simNodes.map((n) => [n.id, n]));
        const simLinks = rawEdges
          .filter((e) => byId.has(e.source) && byId.has(e.target))
          .map((e) => ({ source: e.source, target: e.target }));

        // v3.99c.68 — Cluster mode: assign each node to a cluster bucket
        // and pin each cluster to a separate gravitational center so the
        // force simulation pulls related strains into visible groups.
        // Bucket priority (first match wins):
        //   1. Landrace / heirloom / preservation  → foundation cluster
        //   2. priority_canonical                  → canonical cluster
        //   3. has_listings                        → live-listings cluster
        //   4. strain_family (top 6 by count)      → family-N cluster
        //   5. breeder vendor id (top 4 by count)  → breeder-N cluster
        //   6. else                                → misc cluster
        const isCluster = viewMode === "cluster";
        let clusterCenters = new Map();
        if (isCluster) {
          // Tally families and breeders
          const famCount = new Map();
          const breedCount = new Map();
          for (const n of simNodes) {
            if (n.is_stub) continue;
            const fam = (n.strain_family || "").trim();
            if (fam) famCount.set(fam, (famCount.get(fam) || 0) + 1);
          }
          const topFams = [...famCount.entries()].sort((a, b) => b[1] - a[1]).slice(0, 6).map(([f]) => f);
          const famSet = new Set(topFams);
          // Build cluster ids per node.
          const clusterIds = new Set();
          for (const n of simNodes) {
            let cid;
            if (n.landrace || n.heirloom || n.preservation) cid = "foundation";
            else if (n.priority_canonical) cid = "canonical";
            else if (n.has_listings) cid = "live-listings";
            else if (n.strain_family && famSet.has(n.strain_family)) cid = `family:${n.strain_family}`;
            else cid = "misc";
            n.__cluster = cid;
            clusterIds.add(cid);
          }
          // Spread cluster centers on a circle around the canvas center.
          const cidList = [...clusterIds];
          // Sort: foundation, canonical, live-listings first, then families, then misc last.
          const order = (k) => {
            if (k === "foundation") return 0;
            if (k === "canonical") return 1;
            if (k === "live-listings") return 2;
            if (k.startsWith("family:")) return 3;
            return 9;
          };
          cidList.sort((a, b) => order(a) - order(b) || a.localeCompare(b));
          const R = Math.min(CONSTELLATION_W, CONSTELLATION_H) * 0.32;
          const cx = CONSTELLATION_W / 2;
          const cy = CONSTELLATION_H / 2;
          cidList.forEach((cid, i) => {
            const angle = (i / Math.max(1, cidList.length)) * Math.PI * 2 - Math.PI / 2;
            clusterCenters.set(cid, {
              x: cx + Math.cos(angle) * R,
              y: cy + Math.sin(angle) * R,
            });
          });
        }

        const xForce = forceX((d) => {
          if (isCluster && d.__cluster) {
            const c = clusterCenters.get(d.__cluster);
            if (c) return c.x;
          }
          return CONSTELLATION_W / 2;
        }).strength(isCluster ? 0.22 : 0.04);
        const yForce = forceY((d) => {
          if (isCluster && d.__cluster) {
            const c = clusterCenters.get(d.__cluster);
            if (c) return c.y;
          }
          return CONSTELLATION_H / 2;
        }).strength(isCluster ? 0.22 : 0.04);
        const sim = forceSimulation(simNodes)
          .force("link", forceLink(simLinks).id((d) => d.id).distance(isCluster ? 55 : 70).strength(isCluster ? 0.55 : 0.7))
          .force("charge", forceManyBody().strength(isCluster ? -130 : -180).distanceMax(500))
          .force("center", forceCenter(CONSTELLATION_W / 2, CONSTELLATION_H / 2))
          .force("collide", forceCollide().radius((d) => (d.is_stub ? 12 : 16 + Math.min(8, Math.sqrt(d.save_count || 0) * 1.6))))
          .force("x", xForce)
          .force("y", yForce)
          .stop();
        for (let i = 0; i < 320; i++) sim.tick();
        // Snap to integer-ish coords and reattach edge endpoint ids
        // (d3 mutates link.source/.target into the node object refs).
        const positionedNodes = simNodes.map((n) => ({
          ...n, x: Math.round(n.x || 0), y: Math.round(n.y || 0),
        }));
        const positionedEdges = simLinks.map((l) => ({
          source: typeof l.source === "object" ? l.source.id : l.source,
          target: typeof l.target === "object" ? l.target.id : l.target,
        }));
        const finalById = new Map(positionedNodes.map((n) => [n.id, n]));
        setConstellation({ nodes: positionedNodes, edges: positionedEdges, byId: finalById, clusters: clusterCenters });
        setSelected(null);
      })
      .catch(() => { if (alive) setConstellation({ nodes: [], edges: [], byId: new Map(), clusters: new Map() }); })
      .finally(() => { if (alive) setConstellationLoading(false); });
    return () => { alive = false; };
  }, [viewMode, breederId, filterBreeder, buildAdvancedParams]);

  // Reset the camera every time the underlying dataset changes so we
  // always start each pedigree fitted to the canvas.
  useEffect(() => {
    setView({ zoom: 1, tx: 0, ty: 0 });
  }, [graph.center_id, viewMode, constellation.nodes.length]);

  // v3.99 — Unified view model. In pedigree mode we use the hierarchical
  // computeLayout; in constellation mode we use the force-simulation
  // positions baked into `constellation.nodes`. Returns a graph-shape
  // object so sub-components consume it interchangeably.
  const viewModel = useMemo(() => {
    if (viewMode === "constellation" || viewMode === "cluster") {
      const positions = {};
      for (const n of constellation.nodes) positions[n.id] = { x: n.x, y: n.y, depth: 0, labelTier: 0 };
      return {
        mode: viewMode,
        nodes: constellation.nodes,
        edges: constellation.edges,
        positions,
        clusters: constellation.clusters || new Map(),
        W: CONSTELLATION_W,
        H: CONSTELLATION_H,
        center_id: null,
        node_count: constellation.nodes.length,
        edge_count: constellation.edges.length,
      };
    }
    const ld = computeLayout(graph);
    return {
      mode: "pedigree",
      nodes: graph.nodes || [],
      edges: graph.edges || [],
      positions: ld.positions,
      clusters: new Map(),
      W: ld.width,
      H: ld.height,
      center_id: graph.center_id,
      node_count: (graph.nodes || []).length,
      edge_count: (graph.edges || []).length,
    };
  }, [viewMode, graph, constellation]);
  const layout = viewModel.positions;
  const viewW = viewModel.W;
  const viewH = viewModel.H;

  // v3.99c.68 — Helper booleans for the three view modes. Cluster mode
  // shares the constellation-style force-directed canvas (same node sim
  // shape, same edge straight lines, same loading state); Pedigree mode
  // owns the hierarchical layout.
  const isGraphMode = viewMode === "constellation" || viewMode === "cluster";
  const isPedigree = viewMode === "pedigree";

  // ----- pan/zoom plumbing -----
  const clampZoom = (z) => Math.max(0.3, Math.min(4, z));
  const screenToUser = useCallback((cx, cy) => {
    const svg = svgRef.current;
    if (!svg?.getScreenCTM) return null;
    const ctm = svg.getScreenCTM();
    if (!ctm) return null;
    const pt = svg.createSVGPoint();
    pt.x = cx; pt.y = cy;
    const u = pt.matrixTransform(ctm.inverse());
    return { x: u.x, y: u.y };
  }, []);
  const zoomBy = useCallback((factor, anchor) => {
    setView((v) => {
      const next = clampZoom(v.zoom * factor);
      const real = next / v.zoom;
      if (!anchor) return { ...v, zoom: next };
      return {
        zoom: next,
        tx: anchor.x - (anchor.x - v.tx) * real,
        ty: anchor.y - (anchor.y - v.ty) * real,
      };
    });
  }, []);
  const resetView = useCallback(() => setView({ zoom: 1, tx: 0, ty: 0 }), []);
  const userScalePx = useCallback(() => {
    const svg = svgRef.current;
    if (!svg?.getScreenCTM) return 1;
    const ctm = svg.getScreenCTM();
    return ctm ? (ctm.a || 1) : 1;
  }, []);

  const fitToScreen = useCallback(() => {
    const ids = Object.keys(layout || {});
    if (!ids.length) return;
    let minX = Infinity, maxX = -Infinity, minY = Infinity, maxY = -Infinity;
    for (const id of ids) {
      const p = layout[id];
      const n = viewModel.nodes.find((x) => x.id === id);
      const labelLen = Math.min(22, (n?.label || "").length);
      const pillW = Math.max(60, labelLen * 7.6 + 14);
      const r = 14;
      const left = p.x - Math.max(r, pillW / 2);
      const right = p.x + Math.max(r, pillW / 2);
      const top = p.y - r - 10;
      const bottom = p.y + r + 32 + (p.labelTier === 1 ? 28 : 0);
      if (left < minX) minX = left;
      if (right > maxX) maxX = right;
      if (top < minY) minY = top;
      if (bottom > maxY) maxY = bottom;
    }
    const w = Math.max(1, maxX - minX);
    const h = Math.max(1, maxY - minY);
    const margin = 0.92;
    const zoom = clampZoom(Math.min((viewW / w) * margin, (viewH / h) * margin));
    const cx = (minX + maxX) / 2;
    const cy = (minY + maxY) / 2;
    setView({ zoom, tx: viewW / 2 - zoom * cx, ty: viewH / 2 - zoom * cy });
  }, [layout, viewModel.nodes, viewW, viewH]);

  // Auto-fit whenever a new pedigree finishes rendering.
  useEffect(() => {
    const stillLoading = (viewMode === "pedigree" && (loading || bootstrapping)) ||
                         ((viewMode === "constellation" || viewMode === "cluster") && constellationLoading);
    if (!viewW || !viewH || stillLoading) return undefined;
    const el = svgRef.current;
    if (!el) return undefined;
    const t = setTimeout(() => fitToScreen(), 80);
    return () => clearTimeout(t);
  }, [viewModel.center_id, viewMode, constellation.nodes.length, loading, bootstrapping, constellationLoading, viewW, viewH, fitToScreen]);

  const onPointerDown = (e) => {
    if (e.target.closest?.("[data-testid^='genetics-node-']")) return;
    if (e.target.closest?.("[data-zoom-control]")) return;
    if (e.pointerType === "touch") return;
    e.currentTarget.setPointerCapture?.(e.pointerId);
    dragRef.current = { sx: e.clientX, sy: e.clientY, startTx: view.tx, startTy: view.ty, scale: userScalePx() };
  };
  const onPointerMove = (e) => {
    if (!dragRef.current) return;
    const s = dragRef.current.scale || 1;
    const dx = (e.clientX - dragRef.current.sx) / s;
    const dy = (e.clientY - dragRef.current.sy) / s;
    setView((v) => ({ ...v, tx: dragRef.current.startTx + dx, ty: dragRef.current.startTy + dy }));
  };
  const onPointerUp = (e) => {
    dragRef.current = null;
    try { e.currentTarget.releasePointerCapture?.(e.pointerId); } catch { /* noop */ }
  };
  const onTouchStart = (e) => {
    if (e.touches.length === 2) {
      const dx = e.touches[0].clientX - e.touches[1].clientX;
      const dy = e.touches[0].clientY - e.touches[1].clientY;
      pinchRef.current = {
        dist: Math.hypot(dx, dy),
        zoom: view.zoom,
        cx: (e.touches[0].clientX + e.touches[1].clientX) / 2,
        cy: (e.touches[0].clientY + e.touches[1].clientY) / 2,
      };
    }
    // v3.99c.44 — Single-finger touches on the canvas background NO
    // LONGER capture a pan. This was the actual "touching the black area
    // expands into a black screen" bug: a one-finger drag would set
    // dragRef and onTouchMove would translate the SVG view (tx/ty)
    // unboundedly. Combined with iOS' parallel page scroll (because
    // touchAction is now pan-y), the user's finger movement panned BOTH
    // the page and the map simultaneously, the canvas slid past every
    // node, the background became solid dark, and there was no way back
    // because every additional touch just panned the empty view further.
    // Single-finger pan is now keyboard / mouse / desktop only. Touch
    // users navigate by tapping nodes (clicks fire independent of our
    // touch handler) and pinching to zoom. The new floating Reset
    // button below the canvas is the recovery hatch.
  };
  const onTouchMove = (e) => {
    if (e.touches.length === 2 && pinchRef.current) {
      e.preventDefault();
      const dx = e.touches[0].clientX - e.touches[1].clientX;
      const dy = e.touches[0].clientY - e.touches[1].clientY;
      const dist = Math.hypot(dx, dy);
      const factor = dist / pinchRef.current.dist;
      const next = clampZoom(pinchRef.current.zoom * factor);
      const anchor = screenToUser(pinchRef.current.cx, pinchRef.current.cy);
      if (!anchor) return;
      setView((v) => {
        const real = next / v.zoom;
        return {
          zoom: next,
          tx: anchor.x - (anchor.x - v.tx) * real,
          ty: anchor.y - (anchor.y - v.ty) * real,
        };
      });
    }
    // Single-finger drag intentionally ignored on touch. See onTouchStart.
  };
  const onTouchEnd = () => { pinchRef.current = null; dragRef.current = null; };

  const onWheel = useCallback((e) => {
    e.preventDefault();
    const anchor = screenToUser(e.clientX, e.clientY);
    zoomBy(e.deltaY < 0 ? 1.15 : 1 / 1.15, anchor || undefined);
  }, [zoomBy, screenToUser]);
  useEffect(() => {
    const el = svgRef.current;
    if (!el) return undefined;
    const handler = (e) => onWheel(e);
    el.addEventListener("wheel", handler, { passive: false });
    return () => el.removeEventListener("wheel", handler);
  }, [onWheel]);
  useEffect(() => {
    if (!fullscreen) return undefined;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const onKey = (e) => { if (e.key === "Escape") setFullscreen(false); };
    window.addEventListener("keydown", onKey);
    // v3.99c.41 — Browser back exits fullscreen instead of navigating away
    // from the Atlas page. Push a sentinel history entry on fullscreen
    // enter; popstate (e.g. Android back gesture) closes fullscreen and
    // restores normal page scroll, fixing the "trapped in black screen
    // and the only way out is retyping the domain" report.
    const sentinel = { __cf_map_fullscreen__: true, t: Date.now() };
    try { window.history.pushState(sentinel, ""); } catch { /* noop */ }
    const onPop = () => setFullscreen(false);
    window.addEventListener("popstate", onPop);
    return () => {
      document.body.style.overflow = prev;
      window.removeEventListener("keydown", onKey);
      window.removeEventListener("popstate", onPop);
      // If we're still on the sentinel entry when fullscreen unmounts
      // normally (via the close button), pop it so subsequent back
      // navigation doesn't waste an entry.
      if (window.history.state && window.history.state.__cf_map_fullscreen__) {
        try { window.history.back(); } catch { /* noop */ }
      }
    };
  }, [fullscreen]);

  // ----- node interactions -----
  // Re-center on click. Stubs go through the "promote" flow. We never
  // navigate away from the map on a node click — that's a separate
  // explicit action in the side panel.
  const promoteStub = useCallback(async (node) => {
    if (!node?.is_stub) return;
    const name = (node.label || "").trim();
    if (!name) return;
    if (!user) {
      toast.error("Sign in to suggest a record for this strain");
      nav(`/lineage?suggest=${encodeURIComponent(name)}`);
      return;
    }
    if (!isAdmin) {
      toast.info(`Submit a lineage for "${name}" — it'll go to admin review`);
      nav(`/lineage?suggest=${encodeURIComponent(name)}`);
      return;
    }
    setPromotingId(node.id);
    try {
      const r = await api.post("/admin/lineage/stub/promote", { name });
      const slug = r.data?.slug || r.data?.id;
      if (r.data?.already_existed) toast.success(`Record already existed — re-centering on "${name}"`);
      else toast.success(`Created shell record for "${name}" — re-centering now`);
      if (slug) recenter(slug);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not promote this strain");
    } finally {
      setPromotingId(null);
    }
  }, [user, isAdmin, nav, recenter]);

  const onNodeClick = (n) => {
    if (n.is_stub) { promoteStub(n); return; }
    // v3.99c — In constellation mode, clicking a node drops the user into
    // Pedigree mode anchored on that strain. We CLEAR the nav stack here
    // (instead of pushing the prior cached pedigree center) so a single
    // BACK click returns the user to the constellation library — which
    // is the more intuitive "go back to where you came from" behavior.
    if (viewMode === "constellation" || viewMode === "cluster") {
      setSelected(n.id);
      setNavStack([]);
      setCurrentCenter(n.slug || n.id);
      setViewMode("pedigree");
      return;
    }
    if (n.id === viewModel.center_id) {
      // Already centered: highlight in side panel instead of re-fetching.
      setSelected(n.id);
      return;
    }
    setSelected(n.id);
    recenter(n.slug || n.id);
  };

  // Compute the bloodline path (ancestors + descendants) for the hover/
  // selected node so we can fade everything else.
  const focusedId = hover || selected || viewModel.center_id;
  const lineageSet = useMemo(() => {
    if (!focusedId) return null;
    const parentsOf = new Map();
    const childrenOf = new Map();
    for (const e of (viewModel.edges || [])) {
      if (!childrenOf.has(e.source)) childrenOf.set(e.source, new Set());
      childrenOf.get(e.source).add(e.target);
      if (!parentsOf.has(e.target)) parentsOf.set(e.target, new Set());
      parentsOf.get(e.target).add(e.source);
    }
    const out = new Set([focusedId]);
    const aStack = [focusedId];
    while (aStack.length) {
      const cur = aStack.pop();
      for (const p of (parentsOf.get(cur) || [])) if (!out.has(p)) { out.add(p); aStack.push(p); }
    }
    const dStack = [focusedId];
    while (dStack.length) {
      const cur = dStack.pop();
      for (const c of (childrenOf.get(cur) || [])) if (!out.has(c)) { out.add(c); dStack.push(c); }
    }
    return out;
  }, [focusedId, viewModel.edges]);

  const selectedNode = useMemo(
    () => (viewModel.nodes || []).find((n) => n.id === (selected || viewModel.center_id)),
    [viewModel.nodes, viewModel.center_id, selected]
  );

  // v3.99 — Filter predicates applied at render time. We DIM (not hide)
  // out-of-filter nodes so the lineage context stays visible — important
  // because hiding stub parents or unrelated ancestors breaks the visual
  // sense of "this is where the strain comes from".
  const passesFilters = useCallback((n) => {
    if (filterForSale && !n.has_listings) return false;
    if (filterClass) {
      const bucket = CLASS_BUCKETS.find((b) => b.id === filterClass);
      if (bucket && !bucket.match((n.classification || "").toLowerCase())) return false;
    }
    return true;
  }, [filterForSale, filterClass]);

  // ----- share link -----
  // v3.99c.52 — When centered on a strain, copy the short, SEO-friendly
  // `/g/{slug}` URL instead of the long `?center=…&mode=…&du=…` query.
  // The short URL sets its canonical to /strains/{slug} so any inbound
  // social share routes SEO credit to the canonical strain page.
  // In embedded mode (breeder storefront), the host URL has no map state
  // anyway, so we just copy the host URL.
  const onCopyShareLink = useCallback(() => {
    try {
      let url = window.location.href;
      if (!breederId && currentCenter) {
        const origin = window.location.origin;
        url = `${origin}/g/${currentCenter}`;
      }
      if (navigator.clipboard?.writeText) {
        navigator.clipboard.writeText(url);
        toast.success("Link copied — share this pedigree");
      } else {
        // Fallback for environments without clipboard API.
        const ta = document.createElement("textarea");
        ta.value = url; document.body.appendChild(ta); ta.select();
        document.execCommand("copy"); document.body.removeChild(ta);
        toast.success("Link copied — share this pedigree");
      }
    } catch {
      toast.error("Couldn't copy the link — try again");
    }
  }, [breederId, currentCenter]);

  // ----- render -----
  return (
    <div className={fullscreen ? "fixed inset-0 z-[110] bg-[var(--bg)] flex flex-col" : "bg-[var(--surface)] border border-[var(--border)] overflow-hidden"}
         data-testid="genetics-map"
         data-fullscreen={fullscreen ? "1" : "0"}
         style={fullscreen ? {
           // v3.99c.43 — iOS safe-area padding so the close affordance is
           // never hidden behind the notch / Dynamic Island / home
           // indicator. Even though we now hard-disable fullscreen on
           // coarse-pointer devices, we keep this padding so the desktop-
           // fullscreen experience stays correct if someone resizes a
           // hybrid (touchscreen laptop) into a narrow viewport.
           paddingTop: "env(safe-area-inset-top, 0px)",
           paddingBottom: "env(safe-area-inset-bottom, 0px)",
           paddingLeft: "env(safe-area-inset-left, 0px)",
           paddingRight: "env(safe-area-inset-right, 0px)",
         } : undefined}>

      {fullscreen && (
        <>
          <div className="flex items-center gap-2 px-4 py-3 border-b border-[var(--border)] bg-[var(--surface)]"
               data-testid="genetics-map-fullscreen-bar">
            <Compass className="w-4 h-4" style={{ color: "var(--gold)" }} />
            <span className="font-display text-base md:text-lg" style={{ color: "var(--text)" }}>
              Cannabis Genetics Map
            </span>
            <span className="ml-auto" />
            <button onClick={() => setFullscreen(false)}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 border text-[11px] font-mono uppercase tracking-[0.16em] hover:bg-[var(--bg-2)]"
                    style={{ borderColor: "var(--border-strong)", color: "var(--text-2)", borderRadius: 2 }}
                    data-testid="genetics-map-fullscreen-close">
              <X className="w-3 h-3" /> Close
            </button>
          </div>
          {/* v3.99c.43 — Floating escape hatch. Anchored top-right with
              an extreme z-index and a visible gold pill so the user can
              ALWAYS find a way out of the fullscreen overlay, even if
              the top bar scrolls offscreen or gets covered by an iOS
              system UI element. This is a final-line-of-defence widget. */}
          <button onClick={() => setFullscreen(false)}
                  aria-label="Exit fullscreen map"
                  data-testid="genetics-map-fullscreen-exit-floating"
                  style={{
                    position: "fixed",
                    top: "calc(env(safe-area-inset-top, 0px) + 12px)",
                    right: "calc(env(safe-area-inset-right, 0px) + 12px)",
                    zIndex: 9999,
                    background: "var(--gold)",
                    color: "#000",
                    borderRadius: 999,
                    width: 44, height: 44,
                    display: "inline-flex",
                    alignItems: "center", justifyContent: "center",
                    boxShadow: "0 4px 16px rgba(0,0,0,0.5)",
                  }}>
            <X className="w-5 h-5" />
          </button>
        </>
      )}

      {centerSlug && currentCenter === centerSlug && (
        <div className="px-5 pt-3 -mb-1" data-testid="genetics-map-back-to-strain-row">
          <Link to={`/strains/${centerSlug}`}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 border text-[11px] font-mono uppercase tracking-[0.16em] hover:bg-[var(--bg-2)]"
                style={{ borderColor: "var(--border-strong)", color: "var(--text-2)", borderRadius: 2 }}
                data-testid="genetics-map-back-to-strain">
            <ArrowLeft className="w-3 h-3" /> Back to lineage page
          </Link>
        </div>
      )}

      <PedigreeTopBar
        viewMode={viewMode}
        setViewMode={(m) => { setViewMode(m); setSelected(null); }}
        currentCenter={currentCenter}
        graph={viewModel}
        centerMissing={centerMissing}
        depthUp={depthUp}
        depthDown={depthDown}
        setDepthUp={setDepthUp}
        setDepthDown={setDepthDown}
        setCurrentCenter={(slug) => { setSelected(null); recenter(slug); }}
        fullscreen={fullscreen}
        setFullscreen={requestFullscreen}
        isCoarsePointer={isCoarsePointer}
        filterForSale={filterForSale}
        setFilterForSale={setFilterForSale}
        filterClass={filterClass}
        setFilterClass={setFilterClass}
        filterBreeder={filterBreeder}
        setFilterBreeder={setFilterBreeder}
        filterFamily={filterFamily}
        setFilterFamily={setFilterFamily}
        filterCanonical={filterCanonical}
        setFilterCanonical={setFilterCanonical}
        filterLandrace={filterLandrace}
        setFilterLandrace={setFilterLandrace}
        filterDisputed={filterDisputed}
        setFilterDisputed={setFilterDisputed}
        filterSeedType={filterSeedType}
        setFilterSeedType={setFilterSeedType}
        adminMode={adminMode}
        setAdminMode={setAdminMode}
        adminInternal={adminInternal}
        setAdminInternal={setAdminInternal}
        adminLowConfidence={adminLowConfidence}
        setAdminLowConfidence={setAdminLowConfidence}
        isAdmin={isAdmin}
        breedersDir={breedersDir}
        breederIdLocked={!!breederId}
        onCopyShareLink={onCopyShareLink}
        canGoBack={navStack.length > 0 || viewMode === "pedigree"}
        onBack={goBack}
        onViewAll={viewAll}
        navStackDepth={navStack.length}
      />

      <div className={fullscreen
        ? "flex-1 min-h-0 flex flex-col lg:grid lg:grid-cols-[1fr_360px] overflow-y-auto lg:overflow-hidden"
        : "grid grid-cols-1 lg:grid-cols-[1fr_360px]"}>
        {/* Canvas */}
        <div className={fullscreen
          ? "relative min-w-0 flex-shrink-0 lg:flex-shrink lg:min-h-0"
          : "relative min-w-0 border-r-0 lg:border-r border-[var(--border)]"}
             style={{
               background: "radial-gradient(circle at 50% 35%, rgba(212,175,55,0.06), transparent 65%), var(--bg-2)",
               // v3.99v — Mobile fullscreen previously rendered a black box
               // because the SVG's `height:100%` collapsed inside an
               // undefined-height grid row. Pin a viewport-relative min
               // height when fullscreen so the SVG always has room to draw.
               ...(fullscreen ? { minHeight: "55vh" } : {}),
             }}>

          {/* zoom controls */}
          <div className="absolute top-3 right-3 z-10 flex flex-col gap-1 bg-[var(--surface)] border border-[var(--border-strong)] p-1"
               style={{ borderRadius: 2 }}
               data-zoom-control
               data-testid="genetics-map-zoom-controls">
            <button onClick={() => {
                      const rect = svgRef.current?.getBoundingClientRect();
                      const c = rect ? screenToUser(rect.left + rect.width / 2, rect.top + rect.height / 2) : null;
                      zoomBy(1.25, c || undefined);
                    }}
                    className="inline-flex items-center justify-center w-8 h-8 hover:bg-[var(--bg-2)] transition-colors"
                    style={{ color: "var(--text-2)" }}
                    title="Zoom in"
                    data-testid="genetics-map-zoom-in">
              <ZoomIn className="w-4 h-4" />
            </button>
            <button onClick={() => {
                      const rect = svgRef.current?.getBoundingClientRect();
                      const c = rect ? screenToUser(rect.left + rect.width / 2, rect.top + rect.height / 2) : null;
                      zoomBy(1 / 1.25, c || undefined);
                    }}
                    className="inline-flex items-center justify-center w-8 h-8 hover:bg-[var(--bg-2)] transition-colors"
                    style={{ color: "var(--text-2)" }}
                    title="Zoom out"
                    data-testid="genetics-map-zoom-out">
              <ZoomOut className="w-4 h-4" />
            </button>
            <button onClick={resetView}
                    className="inline-flex items-center justify-center w-8 h-8 hover:bg-[var(--bg-2)] transition-colors"
                    style={{ color: "var(--text-2)" }}
                    title="Reset view"
                    data-testid="genetics-map-zoom-reset">
              <RotateCcw className="w-3.5 h-3.5" />
            </button>
            <button onClick={fitToScreen}
                    className="inline-flex items-center justify-center w-8 h-8 hover:bg-[var(--bg-2)] transition-colors"
                    style={{ color: "var(--gold)" }}
                    title="Fit pedigree to screen"
                    data-testid="genetics-map-zoom-fit">
              <Frame className="w-3.5 h-3.5" />
            </button>
            <div className="text-[9px] font-mono text-center mt-0.5"
                 style={{ color: "var(--text-muted)" }}
                 data-testid="genetics-map-zoom-level">
              {Math.round(view.zoom * 100)}%
            </div>
          </div>

          {/* v3.99c.44 — Permanent mobile Fit-to-screen recovery hatch.
              Anchored bottom-center of the canvas with high z-index so it
              is always visible and tappable. Even if some future regression
              re-enables single-finger pan and the user pans the view to
              empty space, this button instantly fits the pedigree back
              into the viewport. Touch-only — desktop already has the
              zoom-control strip on the right edge. */}
          {isCoarsePointer && (
            <button onClick={fitToScreen}
                    aria-label="Fit map to screen"
                    data-testid="genetics-map-mobile-fit-pill"
                    style={{
                      position: "absolute",
                      bottom: 12,
                      left: "50%",
                      transform: "translateX(-50%)",
                      zIndex: 50,
                      background: "var(--gold)",
                      color: "#000",
                      borderRadius: 999,
                      padding: "8px 16px",
                      fontSize: 11,
                      fontFamily: "monospace",
                      letterSpacing: "0.16em",
                      textTransform: "uppercase",
                      boxShadow: "0 4px 14px rgba(0,0,0,0.5)",
                      display: "inline-flex",
                      alignItems: "center",
                      gap: 6,
                    }}>
              <Frame className="w-3 h-3" /> Fit map
            </button>
          )}

          {((isPedigree && (loading || bootstrapping)) || (isGraphMode && constellationLoading)) && (
            <div className="absolute inset-0 z-20 flex items-center justify-center pointer-events-none"
                 data-testid="genetics-map-loading">
              <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-[var(--surface)] border border-[var(--border)] text-[11px] font-mono uppercase tracking-[0.18em]"
                   style={{ color: "var(--text-2)", borderRadius: 2 }}>
                <Loader2 className="w-3 h-3 animate-spin" />
                {viewMode === "cluster" ? "Building clusters…" : (viewMode === "constellation" ? "Wiring the constellation…" : "Loading pedigree…")}
              </div>
            </div>
          )}

          {isPedigree && centerMissing && !loading && (
            <div className="absolute inset-0 flex items-center justify-center px-6 z-10"
                 data-testid="genetics-map-not-found">
              <div className="max-w-md text-center">
                <AlertCircle className="w-6 h-6 mx-auto mb-3" style={{ color: "var(--secondary)" }} />
                <div className="font-display text-xl mb-1" style={{ color: "var(--text)" }}>
                  Strain not in the public atlas
                </div>
                <p className="text-sm" style={{ color: "var(--text-2)" }}>
                  Pick a different strain from the search above to map its pedigree.
                </p>
              </div>
            </div>
          )}

          {/* v3.99c.41 — Empty-state safety net. If the graph loads but
              returns 0 nodes (network blip, filtered to nothing, or a
              backend hiccup), render an explicit "no nodes to draw"
              card with a Reset button — INSTEAD of the previous behavior
              of showing pure blank canvas which read as a black screen
              on mobile. Fixes the "tapping the map turns it into a
              black screen and you can't escape" report. */}
          {!((isPedigree && (loading || bootstrapping)) ||
             (isGraphMode && constellationLoading)) &&
            !centerMissing && (viewModel.nodes?.length || 0) === 0 && (
            <div className="absolute inset-0 flex items-center justify-center px-6 z-10"
                 data-testid="genetics-map-empty">
              <div className="max-w-md text-center">
                <Compass className="w-7 h-7 mx-auto mb-3" style={{ color: "var(--gold)" }} />
                <div className="font-display text-xl mb-1" style={{ color: "var(--text)" }}>
                  No strains match your filters
                </div>
                <p className="text-sm mb-4" style={{ color: "var(--text-2)" }}>
                  Try clearing your filters or picking a canonical staple from the search bar above.
                </p>
                <div className="flex flex-wrap gap-2 justify-center">
                  <button onClick={() => { setFilterClass(""); setFilterForSale(false);
                                            setFilterFamily(""); setFilterCanonical(false);
                                            setFilterLandrace(false); setFilterDisputed(false);
                                            setFilterSeedType("");
                                            if (!breederId) setFilterBreeder(""); }}
                          className="inline-flex items-center gap-1.5 px-3 py-2 border text-[11px] font-mono uppercase tracking-[0.16em] hover:bg-[var(--bg-2)] transition-colors"
                          style={{ borderColor: "var(--gold)", color: "var(--gold)", borderRadius: 2 }}
                          data-testid="genetics-map-empty-clear-filters">
                    Clear filters
                  </button>
                  <button onClick={() => { setSelected(null); recenter("gmo"); }}
                          className="inline-flex items-center gap-1.5 px-3 py-2 border text-[11px] font-mono uppercase tracking-[0.16em] hover:bg-[var(--bg-2)] transition-colors"
                          style={{ borderColor: "var(--border-strong)", color: "var(--text-2)", borderRadius: 2 }}
                          data-testid="genetics-map-empty-jump-canon">
                    Jump to GMO
                  </button>
                  {fullscreen && (
                    <button onClick={() => setFullscreen(false)}
                            className="inline-flex items-center gap-1.5 px-3 py-2 border text-[11px] font-mono uppercase tracking-[0.16em] hover:bg-[var(--bg-2)] transition-colors"
                            style={{ borderColor: "var(--border-strong)", color: "var(--text-2)", borderRadius: 2 }}
                            data-testid="genetics-map-empty-exit-fullscreen">
                      <X className="w-3 h-3" /> Exit fullscreen
                    </button>
                  )}
                </div>
              </div>
            </div>
          )}

          {!((isPedigree && (loading || bootstrapping)) || (isGraphMode && constellationLoading)) && !centerMissing && (viewModel.nodes?.length || 0) > 0 && (
            <svg ref={svgRef}
                 viewBox={`0 0 ${viewW} ${viewH}`}
                 className="w-full block"
                 style={{
                   // v3.99c.43 — Bounded mobile height + touchAction
                   // semantics. Previously the SVG used `78vh` height
                   // with `touchAction: none` — on iPhone (vh ≈ 658px on
                   // a 390×844 device once URL bar collapses) the canvas
                   // ended up filling the entire visible viewport, dark
                   // background dominated, and touchAction:none meant
                   // the user's finger could never escape the canvas to
                   // scroll the page. That combination is the reported
                   // "black screen trap". Two fixes:
                   //   (1) Cap the inline map height at 480px on touch
                   //       devices (still tall enough to be useful, short
                   //       enough that the page body underneath stays
                   //       visible so the user can always scroll past).
                   //   (2) Use `touchAction: "pan-y pinch-zoom"` instead
                   //       of `none` so the iOS rubber-band gesture can
                   //       still scroll the page upward/downward — pinch
                   //       is preserved for zoom, single-finger drag is
                   //       still handled by our onPointerDown.
                   height: fullscreen
                     ? "min(85vh, calc(100vh - 80px))"
                     : (isCoarsePointer ? "min(480px, 60vh)" : "min(700px, 78vh)"),
                   minHeight: fullscreen ? "55vh" : (isCoarsePointer ? "360px" : "440px"),
                   touchAction: isCoarsePointer ? "pan-y pinch-zoom" : "none",
                   cursor: dragRef.current ? "grabbing" : "grab",
                 }}
                 preserveAspectRatio="xMidYMid meet"
                 onPointerDown={onPointerDown}
                 onPointerMove={onPointerMove}
                 onPointerUp={onPointerUp}
                 onPointerLeave={onPointerUp}
                 onTouchStart={onTouchStart}
                 onTouchMove={onTouchMove}
                 onTouchEnd={onTouchEnd}
                 data-testid="genetics-map-svg">
              <defs>
                <radialGradient id="cf-anchor-glow" r="0.5">
                  <stop offset="0%" stopColor="rgba(212,175,55,0.5)" />
                  <stop offset="55%" stopColor="rgba(212,175,55,0.08)" />
                  <stop offset="100%" stopColor="rgba(212,175,55,0)" />
                </radialGradient>
                <pattern id="cf-grid" x="0" y="0" width="48" height="48" patternUnits="userSpaceOnUse">
                  <path d="M 48 0 L 0 0 0 48" fill="none" stroke="rgba(244,245,247,0.035)" strokeWidth="1"/>
                </pattern>
              </defs>
              <rect x="0" y="0" width={viewW} height={viewH} fill="url(#cf-grid)" />

              <g transform={`translate(${view.tx} ${view.ty}) scale(${view.zoom})`}
                 data-testid="genetics-map-viewport">

                {/* Edges */}
                <g data-testid="genetics-map-edges">
                  {(viewModel.edges || []).map((e, i) => {
                    const a = layout[e.source];
                    const b = layout[e.target];
                    if (!a || !b) return null;
                    // Pedigree mode uses S-curves (parent above, child below);
                    // constellation mode uses straight lines (positions are
                    // arbitrary 2D, not row-stacked).
                    const path = isPedigree
                      ? `M${a.x} ${a.y} C${a.x} ${a.y + (b.y - a.y) * 0.5}, ${b.x} ${a.y + (b.y - a.y) * 0.5}, ${b.x} ${b.y}`
                      : `M${a.x} ${a.y} L${b.x} ${b.y}`;
                    const inLineage = lineageSet && lineageSet.has(e.source) && lineageSet.has(e.target);
                    const isDim = !!lineageSet && !inLineage;
                    const parent = viewModel.nodes.find((n) => n.id === e.source);
                    const child = viewModel.nodes.find((n) => n.id === e.target);
                    const isFiltered = !(passesFilters(parent || {}) && passesFilters(child || {}));
                    const pickColor = (n) => {
                      if (!n || n.is_stub) return null;
                      const c = nodeAccent(n);
                      if (c === "#7F838B" || c === "#6B6E73") return null;
                      return c;
                    };
                    const edgeColor = pickColor(parent) || pickColor(child) || "#D4AF37";
                    const baseOpacity = isFiltered ? 0.1 : (isDim ? 0.25 : 0.95);
                    const haloOpacity = isFiltered ? 0.05 : (isDim ? 0.1 : 0.5);
                    return (
                      <g key={i} data-testid={`genetics-edge-${e.source}-${e.target}`}
                         style={{ transition: "opacity 220ms ease-out" }}>
                        <title>{(parent?.label || "Parent")} → {(child?.label || "Child")}</title>
                        <path d={path} fill="none"
                              stroke="rgba(255,255,255,0.3)"
                              strokeWidth={inLineage ? 6 : (isGraphMode ? 3 : 5)}
                              strokeLinecap="round"
                              opacity={haloOpacity} />
                        <path d={path} fill="none"
                              stroke={edgeColor}
                              strokeWidth={inLineage ? 3 : (isGraphMode ? 1.4 : 2.6)}
                              strokeLinecap="round"
                              opacity={baseOpacity} />
                      </g>
                    );
                  })}
                </g>

                {/* Nodes */}
                <g data-testid="genetics-map-nodes">
                  {(viewModel.nodes || []).map((n) => {
                    const p = layout[n.id];
                    if (!p) return null;
                    const isStub = !!n.is_stub;
                    const isAnchor = n.id === viewModel.center_id;
                    const isHover = hover === n.id;
                    const isSelected = selected === n.id;
                    const isFocused = isAnchor || isHover || isSelected;
                    const baseR = isStub ? 11 : 14 + Math.min(10, Math.sqrt(n.save_count || 0) * 1.8);
                    // Constellation mode: smaller nodes (many of them), no
                    // anchor-only treatment (no single anchor), labels only
                    // on hover/select to avoid label soup.
                    const r = isGraphMode ? Math.max(6, baseR - 4) : baseR;
                    const inLineage = lineageSet && lineageSet.has(n.id);
                    const isFaded = !!lineageSet && !inLineage;
                    const isFiltered = !passesFilters(n);
                    const showLabel = isPedigree || isFocused || (n.save_count || 0) >= 8;
                    const labelText = truncateLabel(n.label, 22) +
                      (isStub ? (isAdmin ? " · Tap to add" : " · Tap to suggest") : "");
                    const fontPx = isAnchor ? 15 : (isGraphMode ? 11 : 13);
                    const pillW = Math.max(60, labelText.length * (isStub ? 6.6 : 7.4) + 16);
                    const pillH = fontPx + 8;
                    const tier = p.labelTier || 0;
                    const pillY = r + 12 + (tier === 1 ? pillH + 6 : 0);
                    const opacity = isFiltered ? 0.18 : (isFaded ? 0.22 : 1);
                    return (
                      <g key={n.id}
                         transform={`translate(${p.x},${p.y})`}
                         onMouseEnter={(e) => { setHover(n.id); setHoverPos({ x: e.clientX, y: e.clientY }); }}
                         onMouseMove={(e) => { if (e.pointerType !== "touch") setHoverPos({ x: e.clientX, y: e.clientY }); }}
                         onMouseLeave={() => { setHover(null); setHoverPos(null); }}
                         onClick={() => onNodeClick(n)}
                         style={{
                           cursor: isStub ? (promotingId === n.id ? "wait" : "pointer") : "pointer",
                           opacity,
                           transition: "opacity 220ms ease-out",
                         }}
                         data-testid={`genetics-node-${n.id}`}
                         data-stub={isStub ? "1" : "0"}
                         data-anchor={isAnchor ? "1" : "0"}>
                        {isAnchor && <circle r={r + 22} fill="url(#cf-anchor-glow)" />}
                        {isAnchor && (
                          <circle r={r + 9} fill="none"
                                  stroke="rgba(212,175,55,0.85)"
                                  strokeWidth={1.5}
                                  strokeDasharray="4 4"
                                  data-testid={`genetics-node-anchor-${n.id}`}/>
                        )}
                        {!isStub && <circle r={r + 3} fill={statusGlow(n)} opacity={isFocused ? 0.85 : (isGraphMode ? 0.18 : 0.32)} />}
                        <circle r={r}
                                fill={isStub ? "rgba(127,131,139,0.18)" : nodeAccent(n)}
                                stroke={isAnchor ? "#D4AF37" : nodeAccent(n)}
                                strokeWidth={isAnchor ? 2.5 : 1}
                                strokeDasharray={isStub ? "3 3" : undefined} />
                        {isStub && (
                          <g pointerEvents="none">
                            <circle cx={r - 2} cy={-(r - 2)} r={5}
                                    fill={isFocused ? "#D4AF37" : "rgba(212,175,55,0.85)"}
                                    stroke="#0B0B0B" strokeWidth={1} />
                            <text x={r - 2} y={-(r - 2) + 3} textAnchor="middle"
                                  style={{ fontFamily: "var(--font-mono, monospace)", fontSize: 9, fontWeight: 800, fill: "#0B0B0B" }}>+</text>
                          </g>
                        )}
                        {!isStub && n.has_listings && (
                          <circle r={r + 6} fill="none" stroke="rgba(79,168,111,0.85)" strokeWidth={0.9} strokeDasharray="2 3"/>
                        )}
                        {!isStub && n.verification_status === "verified" && (
                          <circle r={2.5} cx={r - 2} cy={-(r - 2)} fill="#4FA86F" />
                        )}

                        {/* label pill */}
                        {showLabel && (
                          <>
                            {tier === 1 && (
                              <line x1={0} y1={r + 2} x2={0} y2={pillY - 2}
                                    stroke="rgba(244,245,247,0.22)" strokeWidth={0.8} />
                            )}
                            <rect x={-pillW / 2} y={pillY} width={pillW} height={pillH} rx={3}
                                  fill={isAnchor ? "rgba(212,175,55,0.95)" : "rgba(11,11,11,0.92)"}
                                  stroke={isFocused ? "rgba(212,175,55,0.85)" : "rgba(244,245,247,0.18)"}
                                  strokeWidth={isFocused ? 1 : 0.6} />
                            <text x={0} y={pillY + pillH / 2 + fontPx / 3} textAnchor="middle"
                                  style={{
                                    fontFamily: "var(--font-mono, monospace)",
                                    fontSize: fontPx,
                                    fontWeight: 700,
                                    letterSpacing: "0.04em",
                                    fontStyle: isStub ? "italic" : "normal",
                                    pointerEvents: "none",
                                  }}
                                  fill={isAnchor ? "#0B0B0B" : (isStub ? "rgba(244,245,247,0.78)" : "#F4F5F7")}>
                              {labelText}
                            </text>
                          </>
                        )}
                      </g>
                    );
                  })}
                </g>
              </g>
            </svg>
          )}

          {/* v3.99c.42 — Hover tooltip pop-card. Renders fixed near the
              cursor whenever a strain node is hovered (desktop only — we
              skip when pointer:coarse since touch devices don't have
              hover semantics and would otherwise show ghost cards after
              a tap). Stays inside the viewport via right/bottom edge
              clamping. Pointer-events:none so it never blocks the click
              that opens the larger side-panel detail. */}
          {hover && hoverPos && (() => {
            const node = (viewModel.nodes || []).find((n) => n.id === hover);
            if (!node) return null;
            return <HoverTooltip node={node} pos={hoverPos} />;
          })()}

          {/* legend strip — minimal, bottom */}
          <div className="flex flex-wrap items-center gap-x-4 gap-y-1 px-4 py-2 border-t border-[var(--border)] text-[10px] font-mono uppercase tracking-[0.16em]"
               style={{ color: "var(--text-muted)" }}
               data-testid="genetics-map-legend">
            <LegendDot color={CLASS_COLORS.indica} label="Indica" />
            <LegendDot color={CLASS_COLORS.sativa} label="Sativa" />
            <LegendDot color={CLASS_COLORS.hybrid} label="Hybrid" />
            <LegendDot color="#4FA86F" label="On CannaForge" ring="emerald" />
            <LegendDot color="#D4AF37" label="Landrace" />
            <span className="ml-auto hidden md:inline">
              {viewMode === "cluster"
                ? "Click any node to drop into its pedigree · clusters grouped by family, foundation, listings, and canonical staples"
                : (viewMode === "constellation"
                    ? "Click any node to drop into its pedigree · drag to pan · scroll / pinch to zoom"
                    : "Click a node to re-center · drag to pan · scroll / pinch to zoom")}
            </span>
          </div>

          {/* v3.99c.68 — Cluster mode overlay labels. Each cluster gets a
              small label above its gravitational center so users can
              orient themselves ("ah, this group is the Cookies family"). */}
          {viewMode === "cluster" && viewModel.clusters && viewModel.clusters.size > 0 && (
            <ClusterLabels clusters={viewModel.clusters} view={view} viewW={viewW} viewH={viewH} />
          )}
        </div>

        {/* Side panel */}
        <PedigreeSidePanel
          node={selectedNode}
          graph={viewModel}
          loading={(isPedigree ? (loading || bootstrapping) : constellationLoading)}
          mode={viewMode}
          onOpenLineage={() => { if (selectedNode && !selectedNode.is_stub) nav(`/strains/${selectedNode.slug || selectedNode.id}`); }}
          onReCenter={(slug) => { setSelected(null); recenter(slug); }}
          onPromoteStub={() => selectedNode && promoteStub(selectedNode)}
          promoting={promotingId === selectedNode?.id}
          isAdmin={isAdmin}
          isAuthed={!!user}
          includeSiblings={includeSiblings}
          onToggleSiblings={() => setIncludeSiblings((v) => !v)}
        />
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────
// Top bar — strain picker + depth controls + fullscreen
// ─────────────────────────────────────────────────────────────────────────
function PedigreeTopBar({
  viewMode, setViewMode,
  currentCenter, graph, centerMissing,
  depthUp, depthDown, setDepthUp, setDepthDown,
  setCurrentCenter, fullscreen, setFullscreen, isCoarsePointer,
  filterForSale, setFilterForSale,
  filterClass, setFilterClass,
  filterBreeder, setFilterBreeder,
  filterFamily, setFilterFamily,
  filterCanonical, setFilterCanonical,
  filterLandrace, setFilterLandrace,
  filterDisputed, setFilterDisputed,
  filterSeedType, setFilterSeedType,
  adminMode, setAdminMode,
  adminInternal, setAdminInternal,
  adminLowConfidence, setAdminLowConfidence,
  isAdmin,
  breedersDir, breederIdLocked,
  onCopyShareLink,
  canGoBack, onBack, onViewAll, navStackDepth,
}) {
  const centerNode = (graph.nodes || []).find((n) => n.id === graph.center_id);
  const isConstellation = viewMode === "constellation";
  const isCluster = viewMode === "cluster";
  const isGraphMode = isConstellation || isCluster;
  // Build a small family option list from the currently-loaded graph so
  // the dropdown only shows families that actually exist in view.
  const familyOptions = useMemo(() => {
    const set = new Map();
    for (const n of (graph.nodes || [])) {
      if (n.strain_family) {
        const k = n.strain_family.trim();
        if (k) set.set(k.toLowerCase(), k);
      }
    }
    return [...set.values()].sort((a, b) => a.localeCompare(b));
  }, [graph.nodes]);
  return (
    <div className="border-b border-[var(--border)]"
         data-testid="genetics-map-topbar">

      {/* Row 1 — back / view-all nav + view mode pill + primary control + share */}
      <div className="px-4 md:px-5 py-3 flex flex-wrap items-center gap-3 min-w-0">

        {/* v3.99c — Navigation history controls. "Back" pops the stack of
            previously-anchored strains (and drops to constellation when
            the stack is empty); "View all" is an always-visible express
            lane back to the full genetic map library. Both are hidden
            when we're already in constellation with empty history. */}
        {!isGraphMode && (
          <div className="inline-flex items-center gap-1.5">
            {canGoBack && (
              <button onClick={onBack}
                      className="inline-flex items-center gap-1.5 px-2.5 py-1 border hover:bg-[var(--bg-2)] transition-colors text-[10px] font-mono uppercase tracking-[0.18em]"
                      style={{ borderColor: "var(--border-strong)", color: "var(--text-2)", borderRadius: 2 }}
                      title={navStackDepth > 0
                        ? `Back to the previous strain (${navStackDepth} step${navStackDepth === 1 ? "" : "s"} in history)`
                        : "Back to the full genetic map library"}
                      data-testid="genetics-map-back">
                <ArrowLeft className="w-3 h-3" />
                Back
                {navStackDepth > 0 && (
                  <span className="opacity-60">·{navStackDepth}</span>
                )}
              </button>
            )}
            <button onClick={onViewAll}
                    className="inline-flex items-center gap-1.5 px-2.5 py-1 border hover:bg-[var(--bg-2)] transition-colors text-[10px] font-mono uppercase tracking-[0.18em]"
                    style={{ borderColor: "var(--border-strong)", color: "var(--gold)", borderRadius: 2 }}
                    title="Jump to the full genetic map library"
                    data-testid="genetics-map-view-all">
              <Globe className="w-3 h-3" />
              View all
            </button>
          </div>
        )}

        <div className="inline-flex border border-[var(--border-strong)] overflow-hidden"
             style={{ borderRadius: 999 }}
             data-testid="genetics-map-view-mode">
          <button onClick={() => setViewMode("pedigree")}
                  className="px-3 py-1.5 inline-flex items-center gap-1.5 text-[10px] font-mono uppercase tracking-[0.18em] transition-colors"
                  style={{
                    background: viewMode === "pedigree" ? "var(--gold)" : "transparent",
                    color: viewMode === "pedigree" ? "#0B0B0B" : "var(--text-2)",
                  }}
                  data-testid="genetics-map-view-pedigree">
            <Network className="w-3 h-3" /> Pedigree
          </button>
          <button onClick={() => setViewMode("constellation")}
                  className="px-3 py-1.5 inline-flex items-center gap-1.5 text-[10px] font-mono uppercase tracking-[0.18em] transition-colors border-l border-[var(--border-strong)]"
                  style={{
                    background: isConstellation ? "var(--gold)" : "transparent",
                    color: isConstellation ? "#0B0B0B" : "var(--text-2)",
                  }}
                  data-testid="genetics-map-view-constellation">
            <Globe className="w-3 h-3" /> Constellation
          </button>
          <button onClick={() => setViewMode("cluster")}
                  className="px-3 py-1.5 inline-flex items-center gap-1.5 text-[10px] font-mono uppercase tracking-[0.18em] transition-colors border-l border-[var(--border-strong)]"
                  style={{
                    background: isCluster ? "var(--gold)" : "transparent",
                    color: isCluster ? "#0B0B0B" : "var(--text-2)",
                  }}
                  title="Group strains into clusters by family, foundation, listings, and canonical staples"
                  data-testid="genetics-map-view-cluster">
            <Hexagon className="w-3 h-3" /> Cluster
          </button>
        </div>

        {isGraphMode ? (
          <div className="font-mono text-[11px] uppercase tracking-[0.18em] flex items-center gap-2"
               style={{ color: "var(--text-muted)" }}
               data-testid="genetics-map-constellation-summary">
            {isCluster ? <Hexagon className="w-3.5 h-3.5" style={{ color: "var(--gold)" }} /> : <Globe className="w-3.5 h-3.5" style={{ color: "var(--gold)" }} />}
            <span style={{ color: "var(--text)" }}>{graph.node_count}</span> strains ·
            <span style={{ color: "var(--text)" }}>{graph.edge_count}</span> lineage links
            <span className="hidden md:inline">
              {isCluster ? "· grouped by family / foundation / listings / canonical"
                         : "· click any node to drop into its pedigree"}
            </span>
          </div>
        ) : (
          <StrainPicker
            currentName={centerNode?.label || currentCenter}
            onPick={setCurrentCenter}
          />
        )}

        <div className="ml-auto flex items-center gap-2">
          <button onClick={onCopyShareLink}
                  className="inline-flex items-center gap-1.5 px-2.5 py-1 border hover:bg-[var(--bg-2)] transition-colors text-[10px] font-mono uppercase tracking-[0.18em]"
                  style={{ borderColor: "var(--border)", color: "var(--text-2)", borderRadius: 2 }}
                  title="Copy a shareable link to this view"
                  data-testid="genetics-map-share">
            <Share2 className="w-3 h-3" /> Share
          </button>
          {!fullscreen && !isCoarsePointer && (
            <button onClick={() => setFullscreen(true)}
                    className="inline-flex items-center gap-1 px-2 py-1 border hover:bg-[var(--bg-2)] transition-colors text-[10px] font-mono uppercase tracking-[0.18em]"
                    style={{ borderColor: "var(--border)", color: "var(--text-2)", borderRadius: 2 }}
                    data-testid="genetics-map-fullscreen-open">
              <Maximize2 className="w-3 h-3" /> Expand
            </button>
          )}
        </div>
      </div>

      {/* Row 2 — context-aware controls: pedigree depths, classification chips,
          for-sale toggle, breeder dropdown. */}
      <div className="px-4 md:px-5 pb-3 flex flex-wrap items-center gap-2 min-w-0">

        {!isGraphMode && (
          <div className="inline-flex items-center gap-1 border border-[var(--border)] text-[10px] font-mono uppercase tracking-[0.16em] overflow-x-auto max-w-full"
               style={{ borderRadius: 2, color: "var(--text-muted)" }}
               data-testid="genetics-map-depth-controls">
            <span className="px-2 py-1 shrink-0 hidden sm:inline" style={{ color: "var(--text-2)" }}>Ancestors</span>
            <span className="px-2 py-1 shrink-0 sm:hidden" style={{ color: "var(--text-2)" }}>↑</span>
            {[1, 2, 3, 4].map((n) => (
              <button key={n}
                      onClick={() => setDepthUp(n)}
                      data-testid={`genetics-map-depth-up-${n}`}
                      className="px-2 py-1 transition-colors border-l border-[var(--border)] shrink-0"
                      style={{
                        color: depthUp === n ? "var(--gold)" : "var(--text-muted)",
                        background: depthUp === n ? "rgba(212,175,55,0.08)" : "transparent",
                      }}>
                {n}
              </button>
            ))}
            <span className="px-2 py-1 border-l border-[var(--border)] shrink-0 hidden sm:inline" style={{ color: "var(--text-2)" }}>Descendants</span>
            <span className="px-2 py-1 border-l border-[var(--border)] shrink-0 sm:hidden" style={{ color: "var(--text-2)" }}>↓</span>
            {[0, 1, 2, 3].map((n) => (
              <button key={n}
                      onClick={() => setDepthDown(n)}
                      data-testid={`genetics-map-depth-down-${n}`}
                      className="px-2 py-1 transition-colors border-l border-[var(--border)] shrink-0"
                      style={{
                        color: depthDown === n ? "var(--gold)" : "var(--text-muted)",
                        background: depthDown === n ? "rgba(212,175,55,0.08)" : "transparent",
                      }}>
                {n}
              </button>
            ))}
          </div>
        )}

        {/* Classification chips (also legend) */}
        <div className="inline-flex items-center gap-1 border border-[var(--border)] overflow-x-auto"
             style={{ borderRadius: 2 }}
             data-testid="genetics-map-class-filter">
          {CLASS_BUCKETS.map((b) => {
            const active = filterClass === b.id;
            return (
              <button key={b.id}
                      onClick={() => setFilterClass(active ? "" : b.id)}
                      className="px-2.5 py-1 inline-flex items-center gap-1.5 text-[10px] font-mono uppercase tracking-[0.18em] transition-colors border-l first:border-l-0 border-[var(--border)] shrink-0"
                      style={{
                        color: active ? "var(--gold)" : "var(--text-2)",
                        background: active ? "rgba(212,175,55,0.08)" : "transparent",
                      }}
                      data-testid={`genetics-map-class-${b.id}`}>
                <span className="inline-block w-2 h-2 rounded-full" style={{ background: b.color }} />
                {b.label}
              </button>
            );
          })}
        </div>

        {/* For-sale toggle */}
        <button onClick={() => setFilterForSale((v) => !v)}
                className="inline-flex items-center gap-1.5 px-2.5 py-1 border text-[10px] font-mono uppercase tracking-[0.18em] transition-colors shrink-0"
                style={{
                  borderColor: filterForSale ? "var(--primary-text)" : "var(--border)",
                  color: filterForSale ? "var(--primary-text)" : "var(--text-2)",
                  background: filterForSale ? "rgba(79,168,111,0.08)" : "transparent",
                  borderRadius: 2,
                }}
                data-testid="genetics-map-forsale-toggle">
          <ShoppingBag className="w-3 h-3" />
          {filterForSale ? "For sale only" : "Show all"}
        </button>

        {/* Breeder dropdown — only when not pre-scoped via prop. */}
        {!breederIdLocked && (
          <BreederFilter breedersDir={breedersDir}
                         value={filterBreeder}
                         onChange={setFilterBreeder} />
        )}

        {!centerMissing && (graph.node_count > 0) && (
          <span className="ml-auto hidden md:inline-flex items-center gap-1.5 text-[10px] font-mono uppercase tracking-[0.18em] shrink-0"
                style={{ color: "var(--text-muted)" }}>
            <span>Nodes <strong style={{ color: "var(--text)" }}>{graph.node_count}</strong></span>
            <span>· Edges <strong style={{ color: "var(--text)" }}>{graph.edge_count}</strong></span>
          </span>
        )}
      </div>

      {/* v3.99c.68 — Row 3: Advanced filters. Family dropdown, canonical
          / landrace / disputed toggles, seed-type dropdown, and the
          admin-only mode switch. Rendered as a thin sub-row so the
          default top bar stays uncluttered. */}
      <div className="px-4 md:px-5 pb-3 flex flex-wrap items-center gap-2 min-w-0 border-t border-[var(--border)]/40 pt-2"
           data-testid="genetics-map-advanced-filters">
        <span className="text-[9px] font-mono uppercase tracking-[0.22em] shrink-0"
              style={{ color: "var(--text-muted)" }}>
          Advanced
        </span>

        {/* Family dropdown */}
        <select value={filterFamily}
                onChange={(e) => setFilterFamily(e.target.value)}
                className="px-2 py-1 border bg-[var(--surface)] text-[10px] font-mono uppercase tracking-[0.16em] shrink-0"
                style={{
                  borderColor: filterFamily ? "var(--gold)" : "var(--border)",
                  color: filterFamily ? "var(--gold)" : "var(--text-2)",
                  borderRadius: 2,
                }}
                data-testid="genetics-map-family-filter">
          <option value="">All families</option>
          {familyOptions.map((f) => (
            <option key={f} value={f}>{f}</option>
          ))}
        </select>

        {/* Canonical chip */}
        <button onClick={() => setFilterCanonical((v) => !v)}
                className="inline-flex items-center gap-1.5 px-2.5 py-1 border text-[10px] font-mono uppercase tracking-[0.18em] transition-colors shrink-0"
                style={{
                  borderColor: filterCanonical ? "var(--gold)" : "var(--border)",
                  color: filterCanonical ? "var(--gold)" : "var(--text-2)",
                  background: filterCanonical ? "rgba(212,175,55,0.08)" : "transparent",
                  borderRadius: 2,
                }}
                title="Show only canonical staple strains"
                data-testid="genetics-map-canonical-toggle">
          <Sparkles className="w-3 h-3" /> Canonical
        </button>

        {/* Landrace / foundation chip */}
        <button onClick={() => setFilterLandrace((v) => !v)}
                className="inline-flex items-center gap-1.5 px-2.5 py-1 border text-[10px] font-mono uppercase tracking-[0.18em] transition-colors shrink-0"
                style={{
                  borderColor: filterLandrace ? "var(--gold)" : "var(--border)",
                  color: filterLandrace ? "var(--gold)" : "var(--text-2)",
                  background: filterLandrace ? "rgba(212,175,55,0.08)" : "transparent",
                  borderRadius: 2,
                }}
                title="Show only landrace / heirloom / preservation strains"
                data-testid="genetics-map-landrace-toggle">
          <Mountain className="w-3 h-3" /> Foundation
        </button>

        {/* Disputed chip */}
        <button onClick={() => setFilterDisputed((v) => !v)}
                className="inline-flex items-center gap-1.5 px-2.5 py-1 border text-[10px] font-mono uppercase tracking-[0.18em] transition-colors shrink-0"
                style={{
                  borderColor: filterDisputed ? "#FF7A6A" : "var(--border)",
                  color: filterDisputed ? "#FF7A6A" : "var(--text-2)",
                  background: filterDisputed ? "rgba(255,122,106,0.08)" : "transparent",
                  borderRadius: 2,
                }}
                title="Show only strains with disputed lineage"
                data-testid="genetics-map-disputed-toggle">
          <AlertCircle className="w-3 h-3" /> Disputed
        </button>

        {/* Seed-type dropdown */}
        <select value={filterSeedType}
                onChange={(e) => setFilterSeedType(e.target.value)}
                className="px-2 py-1 border bg-[var(--surface)] text-[10px] font-mono uppercase tracking-[0.16em] shrink-0"
                style={{
                  borderColor: filterSeedType ? "var(--gold)" : "var(--border)",
                  color: filterSeedType ? "var(--gold)" : "var(--text-2)",
                  borderRadius: 2,
                }}
                data-testid="genetics-map-seedtype-filter">
          <option value="">All seed types</option>
          <option value="regular">Regular</option>
          <option value="feminized">Feminized</option>
        </select>

        {/* Clear all advanced */}
        {(filterFamily || filterCanonical || filterLandrace || filterDisputed || filterSeedType) && (
          <button onClick={() => {
                    setFilterFamily(""); setFilterCanonical(false);
                    setFilterLandrace(false); setFilterDisputed(false);
                    setFilterSeedType("");
                  }}
                  className="text-[10px] font-mono uppercase tracking-[0.18em] underline shrink-0"
                  style={{ color: "var(--text-muted)" }}
                  data-testid="genetics-map-advanced-clear">
            Clear
          </button>
        )}

        {/* Admin mode — only rendered for admins. Switching it on
            triggers the backend to include internal/non-public records
            and exposes additional admin-only sub-filters. */}
        {isAdmin && (
          <div className="ml-auto inline-flex items-center gap-2 flex-wrap shrink-0"
               data-testid="genetics-map-admin-mode-block">
            <button onClick={() => setAdminMode(!adminMode)}
                    className="inline-flex items-center gap-1.5 px-2.5 py-1 border text-[10px] font-mono uppercase tracking-[0.18em] transition-colors"
                    style={{
                      borderColor: adminMode ? "var(--secondary)" : "var(--border)",
                      color: adminMode ? "var(--secondary)" : "var(--text-2)",
                      background: adminMode ? "rgba(255,122,106,0.08)" : "transparent",
                      borderRadius: 2,
                    }}
                    title="Toggle admin overlays — only visible to staff"
                    data-testid="genetics-map-admin-mode">
              <BadgeCheck className="w-3 h-3" /> Admin mode
            </button>
            {adminMode && (
              <>
                <button onClick={() => setAdminInternal(!adminInternal)}
                        className="inline-flex items-center gap-1.5 px-2.5 py-1 border text-[10px] font-mono uppercase tracking-[0.18em] transition-colors"
                        style={{
                          borderColor: adminInternal ? "var(--secondary)" : "var(--border)",
                          color: adminInternal ? "var(--secondary)" : "var(--text-2)",
                          background: adminInternal ? "rgba(255,122,106,0.06)" : "transparent",
                          borderRadius: 2,
                        }}
                        title="Include rejected / pending / archived / hidden records"
                        data-testid="genetics-map-admin-internal">
                  Internal
                </button>
                <button onClick={() => setAdminLowConfidence(!adminLowConfidence)}
                        className="inline-flex items-center gap-1.5 px-2.5 py-1 border text-[10px] font-mono uppercase tracking-[0.18em] transition-colors"
                        style={{
                          borderColor: adminLowConfidence ? "var(--secondary)" : "var(--border)",
                          color: adminLowConfidence ? "var(--secondary)" : "var(--text-2)",
                          background: adminLowConfidence ? "rgba(255,122,106,0.06)" : "transparent",
                          borderRadius: 2,
                        }}
                        title="Show only low-confidence records (<0.7)"
                        data-testid="genetics-map-admin-lowconf">
                  Low confidence
                </button>
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

function BreederFilter({ breedersDir, value, onChange }) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const wrapRef = useRef(null);
  useEffect(() => {
    if (!open) return undefined;
    const handler = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [open]);

  // v3.99e — Backend `/lineage/breeders-directory` returns:
  //   vendors:     [{id, name, slug, count, kind:"vendor"}]
  //   originators: [{id:"originator:<name>", name, count, kind:"originator"}]
  // The previous code read `v.store_name` which doesn't exist on the
  // backend payload — so production showed the bucket label ("Vendor
  // breeder") with no actual breeder name. We now read `name` and group
  // the dropdown into two clearly-separated sections: ON CANNAFORGE
  // (vendors) and OFF-SITE ORIGINATORS (non-vendor breeders attributed
  // only by free-text). A vendor that wasn't a vendor previously may
  // graduate from the originators bucket once they onboard — the
  // backend's de-dup logic already prefers vendor attribution when both
  // exist, so this list always reflects current vendor status.
  const matchQ = (n) => !q || (n.name || "").toLowerCase().includes(q.toLowerCase());
  const vendors = (breedersDir?.vendors || []).filter(matchQ);
  const originators = (breedersDir?.originators || []).filter(matchQ);
  const all = [...(breedersDir?.vendors || []), ...(breedersDir?.originators || [])];
  const selected = all.find((x) => x.id === value);

  return (
    <div ref={wrapRef} className="relative shrink-0" data-testid="genetics-map-breeder-filter">
      <button onClick={() => setOpen((o) => !o)}
              className="inline-flex items-center gap-1.5 px-2.5 py-1 border text-[10px] font-mono uppercase tracking-[0.18em] transition-colors"
              style={{
                borderColor: value ? "var(--gold)" : "var(--border)",
                color: value ? "var(--gold)" : "var(--text-2)",
                background: value ? "rgba(212,175,55,0.08)" : "transparent",
                borderRadius: 2,
              }}
              data-testid="genetics-map-breeder-filter-button">
        <Users className="w-3 h-3" />
        {selected ? truncateLabel(selected.name, 22) : "All breeders"}
        <ChevronDown className="w-3 h-3" />
      </button>
      {open && (
        <div className="absolute top-full left-0 mt-1 min-w-[300px] max-w-[360px] bg-[var(--surface)] border border-[var(--border-strong)] z-30 max-h-[60vh] overflow-y-auto"
             style={{ borderRadius: 2 }}
             data-testid="genetics-map-breeder-filter-dropdown">
          <div className="sticky top-0 bg-[var(--surface)] border-b border-[var(--border)] p-2 flex items-center gap-2">
            <Search className="w-3 h-3" style={{ color: "var(--text-muted)" }} />
            <input value={q} onChange={(e) => setQ(e.target.value)}
                   placeholder="Search breeders…" autoFocus
                   className="bg-transparent border-0 outline-none flex-1 text-xs"
                   style={{ color: "var(--text)" }} />
          </div>
          <button onClick={() => { onChange(""); setOpen(false); setQ(""); }}
                  className="w-full text-left px-3 py-2 hover:bg-[var(--bg-2)] text-xs font-mono uppercase tracking-[0.18em] flex items-center justify-between"
                  style={{ color: value ? "var(--text-2)" : "var(--gold)" }}>
            All breeders {!value && <BadgeCheck className="w-3 h-3"/>}
          </button>

          {/* On-site (vendor) breeders */}
          {vendors.length > 0 && (
            <>
              <div className="px-3 py-1.5 border-t border-b border-[var(--border)] bg-[var(--surface-2)] flex items-center gap-2"
                   data-testid="genetics-map-breeder-filter-vendors-header">
                <span className="inline-block w-1.5 h-1.5 rounded-full"
                      style={{ background: "var(--primary-text)" }}/>
                <span className="text-[9px] font-mono uppercase tracking-[0.18em]"
                      style={{ color: "var(--primary-text)" }}>
                  On CannaForge · {vendors.length}
                </span>
              </div>
              {vendors.map((v) => (
                <BreederRow key={v.id}
                            entry={v}
                            isSelected={v.id === value}
                            onPick={() => { onChange(v.id); setOpen(false); setQ(""); }} />
              ))}
            </>
          )}

          {/* Off-site (originator) breeders */}
          {originators.length > 0 && (
            <>
              <div className="px-3 py-1.5 border-t border-b border-[var(--border)] bg-[var(--surface-2)] flex items-center gap-2"
                   data-testid="genetics-map-breeder-filter-originators-header">
                <span className="inline-block w-1.5 h-1.5 rounded-full"
                      style={{ background: "var(--text-muted)" }}/>
                <span className="text-[9px] font-mono uppercase tracking-[0.18em]"
                      style={{ color: "var(--text-muted)" }}>
                  Off-site originators · {originators.length}
                </span>
              </div>
              {originators.map((o) => (
                <BreederRow key={o.id}
                            entry={o}
                            isSelected={o.id === value}
                            onPick={() => { onChange(o.id); setOpen(false); setQ(""); }} />
              ))}
            </>
          )}

          {vendors.length === 0 && originators.length === 0 && (
            <div className="px-3 py-3 text-xs" style={{ color: "var(--text-muted)" }}>
              No matches.
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function BreederRow({ entry, isSelected, onPick }) {
  const isVendor = entry.kind === "vendor";
  return (
    <button onClick={onPick}
            className="w-full text-left px-3 py-2 hover:bg-[var(--bg-2)] flex items-center justify-between gap-2 transition-colors"
            data-testid={`genetics-map-breeder-filter-option-${entry.id}`}>
      <span className="min-w-0 flex-1">
        <span className="block font-serif text-sm truncate" style={{ color: "var(--text)" }}>
          {entry.name}
        </span>
        <span className="text-[9px] font-mono uppercase tracking-[0.18em] inline-flex items-center gap-1.5"
              style={{ color: "var(--text-muted)" }}>
          {isVendor ? (
            <>
              <span style={{ color: "var(--primary-text)" }}>● Live on CannaForge</span>
              <span>·</span>
              <span>{entry.count} record{entry.count === 1 ? "" : "s"}</span>
            </>
          ) : (
            <>
              <span>○ Off-site originator</span>
              <span>·</span>
              <span>{entry.count} record{entry.count === 1 ? "" : "s"}</span>
            </>
          )}
        </span>
      </span>
      {isSelected && <BadgeCheck className="w-3 h-3 shrink-0" style={{ color: "var(--gold)" }} />}
    </button>
  );
}

// ─────────────────────────────────────────────────────────────────────────
// Strain picker — search-as-you-type combobox using /api/lineage?q=
// ─────────────────────────────────────────────────────────────────────────
function StrainPicker({ currentName, onPick }) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [busy, setBusy] = useState(false);
  const wrapRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    const handler = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [open]);

  // Debounced search.
  useEffect(() => {
    if (!open) return undefined;
    const t = setTimeout(async () => {
      setBusy(true);
      try {
        const r = await api.get("/lineage", { params: { q: q || undefined, limit: 12 } });
        setResults(r.data || []);
      } catch { setResults([]); }
      finally { setBusy(false); }
    }, 180);
    return () => clearTimeout(t);
  }, [q, open]);

  return (
    <div ref={wrapRef} className="relative flex-1 min-w-[220px] max-w-[420px]"
         data-testid="genetics-map-strain-picker">
      <button onClick={() => setOpen(true)}
              className="w-full inline-flex items-center gap-2 px-3 py-2 border bg-[var(--surface-2)] text-left transition-colors hover:border-[var(--gold)]"
              style={{ borderColor: "var(--border)", borderRadius: 2 }}
              data-testid="genetics-map-strain-picker-button">
        <Search className="w-3.5 h-3.5" style={{ color: "var(--text-muted)" }} />
        <span className="font-mono text-[10px] uppercase tracking-[0.18em]"
              style={{ color: "var(--text-muted)" }}>
          Mapping
        </span>
        <span className="font-serif text-base truncate flex-1" style={{ color: "var(--text)" }}>
          {currentName || "Pick a strain…"}
        </span>
        <ChevronDown className="w-3.5 h-3.5 shrink-0" style={{ color: "var(--text-muted)" }} />
      </button>

      {open && (
        <div className="absolute top-full left-0 right-0 mt-1 bg-[var(--surface)] border border-[var(--border-strong)] z-30 max-h-[70vh] overflow-y-auto"
             style={{ borderRadius: 2 }}
             data-testid="genetics-map-strain-picker-dropdown">
          <div className="sticky top-0 bg-[var(--surface)] border-b border-[var(--border)] p-2 flex items-center gap-2">
            <Search className="w-3.5 h-3.5" style={{ color: "var(--text-muted)" }} />
            <input autoFocus
                   value={q}
                   onChange={(e) => setQ(e.target.value)}
                   placeholder="Search strains…"
                   className="bg-transparent border-0 outline-none flex-1 text-sm"
                   style={{ color: "var(--text)" }}
                   data-testid="genetics-map-strain-picker-input" />
            {q && (
              <button onClick={() => setQ("")}
                      className="text-[10px] font-mono uppercase tracking-[0.18em]"
                      style={{ color: "var(--text-muted)" }}>
                <X className="w-3 h-3" />
              </button>
            )}
          </div>
          <div className="p-1">
            {busy && (
              <div className="px-3 py-4 text-[11px] font-mono uppercase tracking-[0.18em] flex items-center gap-2"
                   style={{ color: "var(--text-muted)" }}>
                <Loader2 className="w-3 h-3 animate-spin" /> Searching…
              </div>
            )}
            {!busy && results.length === 0 && (
              <div className="px-3 py-4 text-xs" style={{ color: "var(--text-muted)" }}>
                No matching strains.
              </div>
            )}
            {!busy && results.map((r) => (
              <button key={r.id}
                      onClick={() => { onPick(r.slug || r.id); setOpen(false); setQ(""); }}
                      className="w-full px-3 py-2 text-left hover:bg-[var(--bg-2)] flex items-center gap-3"
                      data-testid={`genetics-map-strain-picker-result-${r.id}`}>
                <span className="inline-block w-2 h-2 rounded-full shrink-0"
                      style={{ background: nodeAccent({ classification: r.classification, has_listings: r.has_listings, landrace: r.landrace }) }}/>
                <span className="min-w-0 flex-1">
                  <span className="block font-serif text-sm truncate" style={{ color: "var(--text)" }}>
                    {r.strain_name}
                  </span>
                  <span className="block text-[10px] font-mono truncate"
                        style={{ color: "var(--text-muted)" }}>
                    {(r.parent_one || r.parent_two)
                      ? `${r.parent_one || "?"} × ${r.parent_two || "?"}`
                      : (r.landrace ? "Landrace" : "Root lineage")}
                  </span>
                </span>
                {r.has_listings && (
                  <span className="text-[9px] font-mono uppercase tracking-[0.16em] shrink-0"
                        style={{ color: "var(--primary-text)" }}>Live</span>
                )}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────
// Side panel — selected strain card + ancestors/descendants quick-jump
// ─────────────────────────────────────────────────────────────────────────
function PedigreeSidePanel({ node, graph, loading, mode, onOpenLineage, onReCenter, onPromoteStub, promoting, isAdmin, isAuthed, includeSiblings, onToggleSiblings }) {
  if (loading) {
    return (
      <aside className="bg-[var(--surface)] p-5 lg:min-h-[640px] flex items-center justify-center"
             data-testid="genetics-map-side-panel">
        <div className="text-[11px] font-mono uppercase tracking-[0.18em]"
             style={{ color: "var(--text-muted)" }}>
          Loading…
        </div>
      </aside>
    );
  }
  if (!node) {
    return (
      <aside className="bg-[var(--surface)] p-5"
             data-testid="genetics-map-side-panel">
        {mode === "constellation" ? (
          <div className="space-y-3">
            <div className="text-[10px] font-mono uppercase tracking-[0.18em]"
                 style={{ color: "var(--gold)" }}>Constellation view</div>
            <div className="font-display text-xl tracking-tight" style={{ color: "var(--text)" }}>
              The whole catalog at a glance
            </div>
            <p className="text-sm" style={{ color: "var(--text-2)" }}>
              Every public strain is a star — connected lines are parent → child relationships,
              colors flag classification, and clusters represent breeding lineages that share
              ancestry.
            </p>
            <ul className="space-y-1.5 text-[12px]" style={{ color: "var(--text-muted)" }}>
              <li>· Drag to pan, scroll / pinch to zoom in on a cluster</li>
              <li>· Hover a strain to highlight its full bloodline</li>
              <li>· Click any strain to drop into its dedicated pedigree</li>
              <li>· Filter by classification, breeder, or "For sale only"</li>
            </ul>
          </div>
        ) : (
          <div className="text-sm" style={{ color: "var(--text-muted)" }}>
            Pick a strain to map its pedigree.
          </div>
        )}
      </aside>
    );
  }

  // Resolve direct parents/children from the graph edges.
  const parents = (graph.edges || [])
    .filter((e) => e.target === node.id)
    .map((e) => graph.nodes.find((n) => n.id === e.source))
    .filter(Boolean);
  const children = (graph.edges || [])
    .filter((e) => e.source === node.id)
    .map((e) => graph.nodes.find((n) => n.id === e.target))
    .filter(Boolean);
  // v3.99c.68 — Sibling resolution. A "sibling" is any other child of any
  // of THIS node's parents that isn't the node itself. We dedupe across
  // parents and skip the selected node. Stubs are kept (they may be the
  // only sibling the backend currently exposes) but are non-clickable in
  // the list (handled by SidePanelList already).
  const siblings = (() => {
    const out = new Map();
    const parentIds = new Set(parents.map((p) => p.id));
    for (const e of (graph.edges || [])) {
      if (!parentIds.has(e.source)) continue;
      if (e.target === node.id) continue;
      const sib = graph.nodes.find((n) => n.id === e.target);
      if (sib && !out.has(sib.id)) out.set(sib.id, sib);
    }
    return [...out.values()];
  })();

  const isAnchor = node.id === graph.center_id;

  return (
    <aside className="bg-[var(--surface)] overflow-y-auto"
           data-testid="genetics-map-side-panel"
           style={{ maxHeight: "min(700px, 78vh)" }}>
      {/* v3.99c — Strain card. The header block is itself a Link to the
          full lineage detail page for non-stub nodes, with a subtle gold
          hover treatment so the user understands the whole card is
          clickable. Action buttons below ("Open lineage page",
          "Re-center", "Promote stub") still handle their dedicated
          intents and stop propagation. */}
      <CardOrDiv to={!node.is_stub ? `/strains/${node.slug || node.id}` : null}
                 className="block p-5 border-b border-[var(--border)] group transition-colors"
                 hoverStyle={!node.is_stub ? { background: "rgba(212,175,55,0.04)" } : null}
                 testid="genetics-map-side-panel-card">
        <div className="flex items-center justify-between gap-2 mb-2">
          <span className="text-[10px] font-mono uppercase tracking-[0.18em] inline-flex items-center gap-1"
                style={{ color: isAnchor ? "var(--gold)" : "var(--text-muted)" }}>
            {node.is_stub ? "Stub parent" : (isAnchor ? "Mapping" : "Selected")}
            {!node.is_stub && (
              <span className="opacity-0 group-hover:opacity-100 transition-opacity text-[10px]"
                    style={{ color: "var(--gold)" }}>
                · open page →
              </span>
            )}
          </span>
          {node.has_listings && (
            <span className="inline-flex items-center gap-1 px-1.5 py-0.5 border text-[9px] font-mono uppercase tracking-[0.16em]"
                  style={{ color: "var(--primary-text)", borderColor: "var(--primary-text)", borderRadius: 2 }}>
              <Package className="w-2.5 h-2.5"/> Live
            </span>
          )}
        </div>
        <h3 className="font-display text-2xl tracking-tighter leading-tight mb-2"
            style={{ color: "var(--text)" }}>
          {node.label}
        </h3>

        {/* Metadata chips */}
        <div className="flex items-center gap-1.5 flex-wrap text-[10px] font-mono uppercase tracking-[0.16em] mb-3"
             style={{ color: "var(--text-muted)" }}>
          {node.verification_status === "verified" && !node.is_stub && (
            <span className="inline-flex items-center gap-1" style={{ color: "var(--primary-text)" }}>
              <BadgeCheck className="w-3 h-3"/> Verified
            </span>
          )}
          {node.verification_status === "disputed" && (
            <span className="inline-flex items-center gap-1" style={{ color: "var(--secondary)" }}>
              <AlertCircle className="w-3 h-3"/> Disputed
            </span>
          )}
          {node.landrace && (
            <span className="inline-flex items-center gap-1" style={{ color: "var(--gold)" }}>
              <Mountain className="w-3 h-3"/> Landrace
            </span>
          )}
          {node.heirloom && <span style={{ color: "var(--gold)" }}>Heirloom</span>}
          {node.preservation && <span style={{ color: "var(--gold)" }}>Preservation</span>}
          {node.classification && (
            <span style={{ color: "var(--text-2)" }}>
              {String(node.classification).replace(/_/g, " ")}
            </span>
          )}
          {(node.save_count > 0) && (
            <span className="inline-flex items-center gap-1" style={{ color: "var(--gold)" }}>
              <Bookmark className="w-3 h-3"/> {node.save_count}
            </span>
          )}
        </div>

        {node.is_stub ? (
          <div>
            <p className="text-[12px] mb-3" style={{ color: "var(--text-muted)" }}>
              No public record yet — referenced as a parent strain. {isAdmin
                ? "Create a shell record now and re-center the map on it."
                : (isAuthed ? "Suggest this strain to admins for inclusion." : "Sign in to suggest this strain for inclusion.")}
            </p>
            <button onClick={(e) => { e.preventDefault(); e.stopPropagation(); onPromoteStub(); }}
                    disabled={promoting}
                    className="inline-flex items-center gap-1.5 px-3 py-2 border text-[11px] font-mono uppercase tracking-[0.16em] hover:bg-[var(--gold)] hover:text-[#0B0B0B] disabled:opacity-60 disabled:cursor-wait transition-colors"
                    style={{ borderColor: "var(--gold)", color: "var(--gold)", borderRadius: 2 }}
                    data-testid="genetics-map-side-panel-promote">
              <Plus className="w-3 h-3"/> {promoting ? "Working…" : (isAdmin ? "Add record" : (isAuthed ? "Suggest strain" : "Sign in to suggest"))}
            </button>
          </div>
        ) : (
          <div className="flex flex-col gap-2">
            {/* v3.99c.41 — Commercial CTA first when the node has live
                listings. Routes to the strain's listings tab so the
                shopper hits price/seed-count/buy-now immediately. */}
            {node.has_listings && (
              <Link to={`/strains/${node.slug || node.id}#listings`}
                    onClick={(e) => e.stopPropagation()}
                    className="inline-flex items-center justify-center gap-1.5 px-3 py-2 text-[11px] font-mono uppercase tracking-[0.18em] transition-colors"
                    style={{ background: "var(--gold)", color: "#000", borderRadius: 2 }}
                    data-testid="genetics-map-side-panel-shop">
                <ShoppingBag className="w-3 h-3"/> Shop available genetics
              </Link>
            )}
            <button onClick={(e) => { e.preventDefault(); e.stopPropagation(); onOpenLineage(); }}
                    className="inline-flex items-center justify-center gap-1.5 px-3 py-2 border text-[11px] font-mono uppercase tracking-[0.18em] hover:bg-[var(--gold)] hover:text-[#0B0B0B] transition-colors"
                    style={{ borderColor: "var(--gold)", color: "var(--gold)", borderRadius: 2 }}
                    data-testid="genetics-map-side-panel-open-lineage">
              <ExternalLink className="w-3 h-3"/> Open lineage page
            </button>
            {!isAnchor && (
              <button onClick={(e) => { e.preventDefault(); e.stopPropagation(); onReCenter(node.slug || node.id); }}
                      className="inline-flex items-center justify-center gap-1.5 px-3 py-2 border text-[11px] font-mono uppercase tracking-[0.18em] hover:bg-[var(--bg-2)] transition-colors"
                      style={{ borderColor: "var(--border)", color: "var(--text-2)", borderRadius: 2 }}
                      data-testid="genetics-map-side-panel-recenter">
                <Compass className="w-3 h-3"/> Map this strain's pedigree
              </button>
            )}
            {/* v3.99c.41 — Cross-link chips into the trait/entity graph.
                Family and classification both have real SEO landing pages
                now, so a click takes the user to a curated commercial-
                first surface for that category. */}
            <div className="flex flex-wrap gap-1.5 pt-1">
              {node.strain_family && (
                <Link to={`/families/${(node.strain_family || "").toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                      onClick={(e) => e.stopPropagation()}
                      className="text-[10px] font-mono uppercase tracking-[0.14em] border border-[var(--border)] hover:border-[var(--gold)]/60 px-2 py-1 transition-colors"
                      style={{ color: "var(--text-2)", borderRadius: 2 }}
                      data-testid="genetics-map-side-panel-family-link">
                  {node.strain_family} family
                </Link>
              )}
              {node.classification && (
                <Link to={`/classifications/${(node.classification || "").toLowerCase().replace(/_/g, "-")}`}
                      onClick={(e) => e.stopPropagation()}
                      className="text-[10px] font-mono uppercase tracking-[0.14em] border border-[var(--border)] hover:border-[var(--gold)]/60 px-2 py-1 transition-colors"
                      style={{ color: "var(--text-2)", borderRadius: 2 }}
                      data-testid="genetics-map-side-panel-class-link">
                  {node.classification.replace(/_/g, " ")}
                </Link>
              )}
            </div>
          </div>
        )}
      </CardOrDiv>

      {/* Parents / descendants quick lists */}
      <div className="p-5 space-y-4">
        <SidePanelList title="Direct parents" icon={ChevronUp}
                       items={parents} onPick={onReCenter}
                       testid="genetics-map-side-panel-parents"
                       emptyHint="No parents in view — increase ancestors depth." />
        <SidePanelList title="Direct descendants" icon={ChevronDown}
                       items={children} onPick={onReCenter}
                       testid="genetics-map-side-panel-children"
                       emptyHint="No descendants in view — increase descendants depth." />

        {/* v3.99c.68 — Siblings panel with expand/collapse. Only visible
            when the side panel is showing the anchor strain (the user
            re-centered on this one) so the sibling toggle controls the
            backend `include_siblings` fetch flag. */}
        {isAnchor && !node.is_stub && (
          <div data-testid="genetics-map-side-panel-siblings-block">
            <div className="flex items-center justify-between mb-2">
              <button onClick={onToggleSiblings}
                      className="inline-flex items-center gap-1.5 text-[10px] font-mono uppercase tracking-[0.18em]"
                      style={{ color: includeSiblings ? "var(--gold)" : "var(--text-muted)" }}
                      data-testid="genetics-map-side-panel-siblings-toggle">
                {includeSiblings ? <ChevronDown className="w-3 h-3" /> : <ChevronUp className="w-3 h-3 rotate-180" />}
                Siblings · {includeSiblings ? siblings.length : "expand"}
              </button>
              {includeSiblings && siblings.length > 0 && (
                <span className="text-[9px] font-mono uppercase tracking-[0.16em]"
                      style={{ color: "var(--text-muted)" }}>
                  other children of {parents.length === 1 ? parents[0].label : `${parents.length} parents`}
                </span>
              )}
            </div>
            {includeSiblings && (
              <SidePanelList title="Sibling strains" icon={Network}
                             items={siblings} onPick={onReCenter}
                             testid="genetics-map-side-panel-siblings"
                             emptyHint="No sibling strains found for this lineage in the public set." />
            )}
            {!includeSiblings && (
              <p className="text-[11px] italic" style={{ color: "var(--text-muted)" }}>
                Click "expand" to see other strains that share this parentage.
              </p>
            )}
          </div>
        )}

        {/* v3.99c.68 — Admin-only metadata block. Surfaces low-confidence,
            non-indexable, duplicate, and hidden flags returned by
            ?admin_mode=true so staff can identify problem records
            without leaving the map. */}
        {isAdmin && !node.is_stub && (node.confidence_score !== undefined ||
                                       node.seo_noindex !== undefined ||
                                       node.is_hidden !== undefined ||
                                       node.duplicate_of) && (
          <div className="border border-[var(--secondary)]/40 bg-[var(--surface-2)] p-3"
               style={{ borderRadius: 2 }}
               data-testid="genetics-map-side-panel-admin">
            <div className="text-[10px] font-mono uppercase tracking-[0.18em] mb-2"
                 style={{ color: "var(--secondary)" }}>
              Admin overlay
            </div>
            <ul className="space-y-1 text-[11px]" style={{ color: "var(--text-2)" }}>
              {node.confidence_score !== undefined && node.confidence_score !== null && (
                <li>Confidence: <strong style={{ color: "var(--text)" }}>{(node.confidence_score * 100).toFixed(0)}%</strong></li>
              )}
              {node.seo_noindex && (
                <li style={{ color: "var(--secondary)" }}>● noindexed (hidden from search)</li>
              )}
              {node.is_hidden && (
                <li style={{ color: "var(--secondary)" }}>● hidden record</li>
              )}
              {node.duplicate_of && (
                <li>Duplicate of: <code style={{ color: "var(--gold)" }}>{node.duplicate_of}</code></li>
              )}
            </ul>
          </div>
        )}
      </div>
    </aside>
  );
}

function SidePanelList({ title, icon: Icon, items, onPick, testid, emptyHint }) {
  return (
    <div data-testid={testid}>
      <div className="text-[10px] font-mono uppercase tracking-[0.18em] mb-2 flex items-center gap-1.5"
           style={{ color: "var(--text-muted)" }}>
        <Icon className="w-3 h-3"/> {title} · <span style={{ color: "var(--text-2)" }}>{items.length}</span>
      </div>
      {items.length === 0 ? (
        <div className="text-[11px] italic" style={{ color: "var(--text-muted)" }}>{emptyHint}</div>
      ) : (
        <ul className="space-y-1">
          {items.map((n) => (
            <li key={n.id}>
              <button onClick={() => onPick(n.slug || n.id)}
                      disabled={n.is_stub}
                      className="w-full text-left px-2.5 py-1.5 border border-[var(--border)] hover:border-[var(--gold)] flex items-center gap-2 transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
                      style={{ borderRadius: 2 }}
                      data-testid={`${testid}-item-${n.id}`}>
                <span className="inline-block w-2 h-2 rounded-full shrink-0"
                      style={{ background: nodeAccent(n) }}/>
                <span className="font-serif text-sm truncate flex-1"
                      style={{ color: n.is_stub ? "var(--text-muted)" : "var(--text)" }}>
                  {n.label}
                </span>
                {n.has_listings && (
                  <ShoppingBag className="w-3 h-3 shrink-0" style={{ color: "var(--primary-text)" }}/>
                )}
                {n.is_stub && (
                  <span className="text-[9px] font-mono uppercase tracking-[0.16em]" style={{ color: "var(--text-muted)" }}>
                    stub
                  </span>
                )}
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

// v3.99c — Tiny dual-purpose wrapper used by the side-panel strain card.
// When `to` is a non-empty string the entire card becomes a navigable
// link surface to the strain detail page; otherwise it falls back to a
// plain div (e.g., for stub nodes that have no public detail page to
// navigate to). The hover state mirrors a subtle "interactive card"
// affordance.
//
// v3.99c.41 — Changed from rendering a real <Link> (which produces an
// <a> element) to a <div role="link"> with a programmatic navigate()
// + key handler. This prevents nested-anchor hydration warnings now
// that the side-panel card hosts inner <Link> chips (shop CTA, family,
// classification) which would otherwise be descendants of an outer <a>.
function CardOrDiv({ to, className, hoverStyle, children, testid }) {
  const nav = useNavigate();
  const [hover, setHover] = useState(false);
  const style = (hover && hoverStyle) ? hoverStyle : undefined;
  const onEnter = () => setHover(true);
  const onLeave = () => setHover(false);
  if (to) {
    const go = (e) => {
      // Don't navigate when the user clicked an actual interactive
      // child (button, link, input) inside the card.
      if (e.defaultPrevented) return;
      if (e.target.closest && e.target.closest("a,button,input,select,[role='link']:not([data-card-root])"))
        return;
      nav(to);
    };
    const onKey = (e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); nav(to); } };
    return (
      <div role="link"
           tabIndex={0}
           data-card-root="1"
           className={className}
           style={{ ...(style || {}), cursor: "pointer" }}
           onMouseEnter={onEnter}
           onMouseLeave={onLeave}
           onClick={go}
           onKeyDown={onKey}
           data-testid={testid}>
        {children}
      </div>
    );
  }
  return (
    <div className={className}
         style={style}
         onMouseEnter={onEnter}
         onMouseLeave={onLeave}
         data-testid={testid}>
      {children}
    </div>
  );
}

function LegendDot({ color, label, ring }) {
  return (
    <span className="inline-flex items-center gap-1.5">
      <span className="inline-block rounded-full" style={{
        width: 8, height: 8, background: color,
        boxShadow: ring === "emerald" ? "0 0 0 2px rgba(79,168,111,0.35)" : "none",
      }} />
      {label}
    </span>
  );
}

// v3.99c.68 — ClusterLabels: overlays a thin chip above each gravitational
// center in Cluster mode so the user can read which group is which.
// Rendered outside the SVG so we don't fight the SVG viewBox; we project
// the cluster's canvas coords through the same translate/scale transform
// the canvas applies via simple math.
function ClusterLabels({ clusters, view, viewW, viewH }) {
  return (
    <div className="absolute inset-0 pointer-events-none" data-testid="genetics-map-cluster-labels">
      <svg viewBox={`0 0 ${viewW} ${viewH}`} className="w-full h-full block"
           preserveAspectRatio="xMidYMid meet">
        <g transform={`translate(${view.tx} ${view.ty}) scale(${view.zoom})`}>
          {[...clusters.entries()].map(([cid, pos]) => {
            const label = cid === "foundation" ? "FOUNDATION"
                       : cid === "canonical" ? "CANONICAL"
                       : cid === "live-listings" ? "LIVE LISTINGS"
                       : cid === "misc" ? "OTHER"
                       : cid.startsWith("family:") ? cid.slice(7).toUpperCase()
                       : cid.toUpperCase();
            const w = Math.max(80, label.length * 7.2 + 16);
            return (
              <g key={cid} transform={`translate(${pos.x}, ${pos.y - 90})`}>
                <rect x={-w / 2} y={-12} width={w} height={22} rx={2}
                      fill="rgba(11,11,11,0.86)"
                      stroke="rgba(212,175,55,0.55)" strokeWidth={1} />
                <text x={0} y={3} textAnchor="middle"
                      style={{
                        fontFamily: "var(--font-mono, monospace)",
                        fontSize: 11, fontWeight: 700,
                        letterSpacing: "0.18em", fill: "#D4AF37",
                      }}>
                  {label}
                </text>
              </g>
            );
          })}
        </g>
      </svg>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────
// computeLayout — hierarchical pedigree layout (proven from v3.97).
// Walks parent→child edges, assigns depth levels relative to graph.center_id,
// and positions nodes in horizontal rows by depth with sub-row wrapping
// for crowded levels.
// ─────────────────────────────────────────────────────────────────────────
function computeLayout(graph) {
  const out = {};
  if (!graph?.nodes?.length) return { positions: out, width: 880, height: 640 };

  const PADDING_X = 80;
  const ROW_GAP = 180;
  const SUBROW_GAP = 110;
  const NODE_GAP_MIN = 200;
  const MAX_NODES_PER_SUBROW = 10;
  const LABEL_CHAR_PX = 7.4;
  const LABEL_PAD = 16;
  const labelWidth = (s) => Math.min(240, Math.max(60, (String(s || "").length) * LABEL_CHAR_PX + LABEL_PAD));

  const parents = {};
  const children = {};
  for (const e of (graph.edges || [])) {
    (parents[e.target] = parents[e.target] || []).push(e.source);
    (children[e.source] = children[e.source] || []).push(e.target);
  }

  // BFS depth from center_id (or first node if center missing).
  const depth = {};
  const seedId = graph.center_id || graph.nodes[0].id;
  depth[seedId] = 0;
  const queue = [seedId];
  while (queue.length) {
    const cur = queue.shift();
    for (const p of (parents[cur] || [])) {
      if (depth[p] === undefined) { depth[p] = depth[cur] - 1; queue.push(p); }
    }
    for (const c of (children[cur] || [])) {
      if (depth[c] === undefined) { depth[c] = depth[cur] + 1; queue.push(c); }
    }
  }
  for (const n of graph.nodes) if (depth[n.id] === undefined) depth[n.id] = 0;

  // Bucket nodes by depth.
  const rows = {};
  for (const n of graph.nodes) (rows[depth[n.id]] = rows[depth[n.id]] || []).push(n.id);
  const sortedDepths = Object.keys(rows).map(Number).sort((a, b) => a - b);

  const idToLabel = Object.fromEntries(graph.nodes.map((n) => [n.id, n.label]));
  const idToNode = Object.fromEntries(graph.nodes.map((n) => [n.id, n]));
  const sortedRowChunks = sortedDepths.map((d) => {
    const sorted = rows[d].slice().sort((a, b) => {
      const na = idToNode[a];
      const nb = idToNode[b];
      if ((na?.is_stub ? 1 : 0) !== (nb?.is_stub ? 1 : 0)) {
        return (na?.is_stub ? 1 : 0) - (nb?.is_stub ? 1 : 0);
      }
      return (na?.label || "").localeCompare(nb?.label || "");
    });
    const chunks = [];
    for (let i = 0; i < sorted.length; i += MAX_NODES_PER_SUBROW) {
      chunks.push(sorted.slice(i, i + MAX_NODES_PER_SUBROW));
    }
    return { depth: d, chunks };
  });

  let W = 880;
  for (const { chunks } of sortedRowChunks) {
    for (const chunk of chunks) {
      const sum = chunk.reduce((acc, id) => acc + Math.max(NODE_GAP_MIN, labelWidth(idToLabel[id])), 0);
      W = Math.max(W, PADDING_X * 2 + sum);
    }
  }

  let y = 0;
  const subRowYs = [];
  sortedRowChunks.forEach(({ depth: d, chunks }, rowIdx) => {
    if (rowIdx > 0) y += ROW_GAP;
    chunks.forEach((chunk, subIdx) => {
      if (subIdx > 0) y += SUBROW_GAP;
      subRowYs.push({ depth: d, chunk, y });
    });
  });
  const totalHeight = y;
  const H = Math.max(640, totalHeight + 220);
  const baseY = (H - totalHeight) / 2;

  for (const sr of subRowYs) {
    const { chunk, y: dy } = sr;
    const slotWidths = chunk.map((id) => Math.max(NODE_GAP_MIN, labelWidth(idToLabel[id])));
    const totalRowW = slotWidths.reduce((a, b) => a + b, 0);
    let cursor = (W - totalRowW) / 2;
    const yPx = baseY + dy;
    chunk.forEach((id, i) => {
      const slotW = slotWidths[i];
      const x = cursor + slotW / 2;
      const labelTier = i % 2 === 0 ? 0 : 1;
      out[id] = { x, y: yPx, depth: sr.depth, slotW, labelTier };
      cursor += slotW;
    });
  }

  return { positions: out, width: W, height: H };
}



/**
 * HoverTooltip — v3.99c.42
 * Desktop-only fixed-position pop-card that appears when the user hovers a
 * strain node on the Genetics Map. Skipped on coarse pointers (touch
 * devices) because hover is meaningless there — touch uses the side
 * panel/bottom sheet instead. Position-clamped to keep the card inside
 * the viewport; pointer-events:none so it never intercepts the click
 * that opens the larger detail panel.
 */
function HoverTooltip({ node, pos }) {
  const ref = useRef(null);
  const [adj, setAdj] = useState({ left: pos.x + 14, top: pos.y + 14 });

  useEffect(() => {
    // Skip rendering on coarse-pointer touch devices.
    if (typeof window !== "undefined" &&
        window.matchMedia && window.matchMedia("(pointer: coarse)").matches) {
      return;
    }
    const el = ref.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const vw = window.innerWidth;
    const vh = window.innerHeight;
    let left = pos.x + 14;
    let top = pos.y + 14;
    if (left + rect.width + 8 > vw) left = pos.x - rect.width - 14;
    if (top + rect.height + 8 > vh) top = pos.y - rect.height - 14;
    if (left < 8) left = 8;
    if (top < 8) top = 8;
    setAdj({ left, top });
  }, [pos.x, pos.y]);

  // Coarse-pointer safety: render nothing on touch.
  if (typeof window !== "undefined" && window.matchMedia &&
      window.matchMedia("(pointer: coarse)").matches) {
    return null;
  }

  const parents = [node.parent_one, node.parent_two].filter(Boolean).join(" × ");
  const isCanon = node.priority_canonical;
  const isLanded = node.landrace || node.heirloom || node.preservation;
  const isDisputed = node.verification_status === "disputed";
  const status = node.is_stub
    ? "Stub · referenced parent"
    : (isCanon ? "Canonical staple"
        : (node.verification_status === "verified" ? "Verified"
            : (node.verification_status === "breeder_submitted" ? "Breeder-submitted"
                : (node.verification_status === "community_submitted" ? "Collector-submitted"
                    : "Reported lineage"))));

  return (
    <div ref={ref}
         role="tooltip"
         data-testid="genetics-map-hover-tooltip"
         style={{
           position: "fixed",
           left: adj.left,
           top: adj.top,
           zIndex: 120,
           pointerEvents: "none",
           maxWidth: 280,
           background: "rgba(11,11,11,0.96)",
           border: `1px solid ${node.has_listings ? "var(--gold)" : "var(--border-strong)"}`,
           borderRadius: 2,
           padding: "10px 12px",
           boxShadow: "0 12px 32px rgba(0,0,0,0.55)",
           backdropFilter: "blur(8px)",
           WebkitBackdropFilter: "blur(8px)",
         }}>
      <div className="flex items-center gap-1.5 mb-1">
        <span className="inline-block w-2 h-2 rounded-full shrink-0"
              style={{ background: nodeAccent(node) }} />
        <div className="font-display text-base leading-tight truncate"
             style={{ color: "var(--text)" }}
             data-testid="hover-tooltip-name">
          {node.label}
        </div>
        {node.has_listings && (
          <span className="ml-auto inline-flex items-center gap-1 px-1.5 py-0.5 text-[9px] font-mono uppercase tracking-[0.14em]"
                style={{ background: "var(--gold)", color: "#000", borderRadius: 2 }}
                data-testid="hover-tooltip-listing-badge">
            ● live
          </span>
        )}
      </div>
      {parents && (
        <div className="text-[11px] mb-1.5 truncate" style={{ color: "var(--text-2)" }}
             data-testid="hover-tooltip-parents">
          {parents}
        </div>
      )}
      <div className="flex flex-wrap gap-1 mb-1">
        {node.classification && (
          <span className="text-[9px] font-mono uppercase tracking-[0.14em] px-1.5 py-0.5"
                style={{ background: "rgba(212,175,55,0.12)", color: "var(--gold)", borderRadius: 2 }}>
            {String(node.classification).replace(/_/g, " ")}
          </span>
        )}
        {node.strain_family && (
          <span className="text-[9px] font-mono uppercase tracking-[0.14em] px-1.5 py-0.5"
                style={{ background: "rgba(143,179,255,0.12)", color: "#8FB3FF", borderRadius: 2 }}>
            {node.strain_family}
          </span>
        )}
      </div>
      <div className="text-[10px] font-mono uppercase tracking-[0.14em] inline-flex items-center gap-1"
           style={{
             color: isCanon ? "var(--gold)"
                 : (isDisputed ? "var(--secondary)"
                     : (isLanded ? "#D4AF37" : "var(--text-muted)")),
           }}
           data-testid="hover-tooltip-status">
        {isCanon && <Sparkles className="w-2.5 h-2.5" />}
        {isLanded && !isCanon && <Mountain className="w-2.5 h-2.5" />}
        {!isCanon && !isLanded && node.verification_status === "verified" && <BadgeCheck className="w-2.5 h-2.5" />}
        {isDisputed && <AlertCircle className="w-2.5 h-2.5" />}
        {status}
      </div>
      {node.has_listings ? (
        <div className="text-[9px] font-mono uppercase tracking-[0.14em] mt-1 pt-1 border-t"
             style={{ color: "var(--gold)", borderColor: "rgba(212,175,55,0.3)" }}>
          ▸ available on CannaForge · click to shop
        </div>
      ) : (
        <div className="text-[9px] font-mono uppercase tracking-[0.14em] mt-1 opacity-60"
             style={{ color: "var(--text-muted)" }}>
          click to map this strain
        </div>
      )}
    </div>
  );
}
